/**
 * Static rule definitions for the promotion rule builder.
 * For backend-aligned attribute names and rule types, see ./backendRuleAttributes.js.
 * Payload sent to backend: rules { ruleType, ifRules, thenRules } (creteria is UI-only).
 */
import { Category, Person, Redeem } from "@mui/icons-material";
import { end } from "bfj/src/events";
import CartIcon from "common/customIcons/cart";
import PaymentIcon from "common/customIcons/payment";
import TimeIcon from "common/customIcons/time";
import { at, cloneDeep } from "lodash";
import moment from "moment";

/***
 * Millis convert into Minutes and Seconds
 * @param {millis}
 * @returns {minutes:seconds}
 */
export const millisToMinutesAndSeconds = (millis) => {
    var minutes = Math.floor(millis / 60000);
    var seconds = ((millis % 60000) / 1000).toFixed(0);
    return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
};

/***
 * Sleep for wait for mileseconds
 * @param {ms} time in mileseconds to sleep
 * @returns {NA}
 */
export const sleep = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

export const imageExtentions = (ext) => {
    let extentions = [
        "jpeg",
        "jpg",
        "png",
        "gif",
        "bmp",
        "webp",
        "tiff",
        "svg",
        "ico",
        "heic",
        "heif",
        "raw",
        "psd",
        "eps",
        "ai",
    ];

    return extentions.includes(ext);
};

export const handleKeyDown = (event) => {
    if (event.key === "Enter") {
        event.preventDefault(); // Prevent form submission
    }
};

export const videoExtentions = (ext) => {
    let extentions = [
        "mp4",
        "avi",
        "mov",
        "wmv",
        "flv",
        "mkv",
        "mpeg",
        "mpg",
        "m4v",
        "3gp",
        "webm",
        "rm",
        "vob",
        "ogv",
        "mts",
        "m2ts",
        "ts",
        "mxf",
        "f4v",
        "divx",
        "xvid",
        "asf",
    ];

    return extentions.includes(ext);
};

export const parseAndCompare = (beforeObj, afterObj) => {
    let before, after;

    try {
        before = beforeObj;
    } catch (error) {
        return { error: "Invalid object in beforeObj" };
    }

    try {
        after = afterObj;
    } catch (error) {
        return { error: "Invalid object in afterObj" };
    }

    let changes = {};
    for (const key in before) {
        if (
            Object.prototype.hasOwnProperty.call(before, key) &&
            Object.prototype.hasOwnProperty.call(after, key)
        ) {
            if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
                changes[key] = { before: before[key], after: after[key] };
            }
        }
    }
    return changes;
};

export const getChanges = (obj) => {
    const changes = obj.changes.map((change) => {
        const before = change.Before;
        const after = change.After;

        let result = [];

        for (const key in after) {
            if (Object.prototype.hasOwnProperty.call(after, key)) {
                result.push({
                    fieldName: key,
                    beforeValue: before[key] || null,
                    afterValue: after[key],
                });
            }
        }

        return result;
    });

    return changes.flat();
};

export const analyzeObjectChanges = (item) => {
    let results = {};

    const { changes } = item;

    for (const change of changes) {
        const { after, before } = change;
        const updatedFields = {};

        // Identify updated fields
        for (const key in after) {
            if (
                before &&
                Object.hasOwnProperty.call(before, key) &&
                after[key] !== before[key]
            ) {
                updatedFields[key] = {
                    previousValue: before[key],
                    newValue: after[key],
                };
            }
        }

        // Handle cases where fields might be added or removed entirely
        if (before) {
            for (const key in before) {
                if (!after || !Object.hasOwnProperty.call(after, key)) {
                    updatedFields[key] = {
                        previousValue: before[key],
                        newValue: null, // Field removed
                    };
                }
            }
        } else if (after) {
            for (const key in after) {
                updatedFields[key] = {
                    previousValue: null, // Field added
                    newValue: after[key],
                };
            }
        }

        results = {
            entityId: item.entity_id,
            updatedFields,
        };
    }

    return results;
};

export const countMatchingFilters = (customFilters, filters) => {
    let count = 0;
    for (let key in customFilters) {
        if (filters?.includes(key)) {
            count++;
        }
    }
    return count;
};

export const scrollToFirstError = (errors) => {
    const errorKeys = Object.keys(errors);

    if (errorKeys.length > 0) {
        let firstErrorKey = errorKeys[0];

        if (firstErrorKey === "languages") {
            errors.languages.map((languageError) => {
                if (languageError) {
                    firstErrorKey = Object.keys(languageError)?.[0];
                }
            });
        }

        const element = document.getElementById(firstErrorKey);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
            element.focus();
        }
    }
};

export const findType = (addressComponents, type) => {
    for (const component of addressComponents) {
        if (component.types.includes(type)) {
            return component;
        }
    }
    return "";
};

export const formatAddress = (address) => {
    const {
        addr1 = "",
        // addr2 = "",
        city = "",
        state = "",
        zip = "",
        country = "",
    } = address || {};
    // Filter out any empty parts and join with commas
    const addressParts = [
        addr1,
        // addr2,
        city,
        state ? `${state} ${zip}` : zip,
        country?.id || country,
    ].filter((part) => part && part.trim()); // only include non-empty parts

    return addressParts.join(", ");
};

export const splitFullName = (fullName) => {
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
    return { firstName, lastName };
};

export const generateSlug = (text) => {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
};

export const formattedNumberToFixed = (number) => {
    if (!number) {
        return number;
    }
    let parts = number.toString().split(".");
    let formattedNumber =
        parts[0] + "." + (parts[1] ? parts[1].substring(0, 2) : "00");
    return formattedNumber;
};

export const languagesSort = (data) => {
    if (!data) {
        return data;
    }

    const languageOrder = ["en", "fr", ""];
    data.languages.sort((a, b) => {
        const indexA = languageOrder.indexOf(a.language);
        const indexB = languageOrder.indexOf(b.language);
        return indexA - indexB;
    });

    return data;
};

export const getFirstEmail = (orderContactEmail) => {
    // Split the string by ';' and trim any whitespace from the first result
    if (!orderContactEmail) {
        return "";
    }
    const firstEmail = orderContactEmail.split(";")[0]?.trim();
    return firstEmail;
};

export const getLangErr = (errors = {}, fieldName = "") => {
    const langErr = errors?.languages?.find(
        (langErr) => !!langErr?.[fieldName],
    )?.[fieldName];

    return langErr;
};

export const getCurrency = (currency) => {
    if (!currency) {
        return "-";
    }

    if (currency === "CAN" || currency === "CAD") {
        return "CAD";
    } else if (["US", "USD", "US Dollar"].includes(currency)) {
        return "USD";
    }

    return currency;
};

export const scrollToDiv = (id) => {
    if (!id) {
        return;
    }

    const targetElm = document.querySelector(`#${id}`);
    targetElm.scrollIntoView({ block: "center" });
};

export const format_date = (date, apiFormat = false) => {
    if (!date || !moment(date).isValid()) {
        return "-";
    }

    if (apiFormat) {
        return moment(date).format("YYYY-MM-DD");
    }
    return moment(date).format("MMM DD, YYYY");
};

export const format_dateTime = (date) => {
    if (!date || !moment(date).isValid()) {
        return "-";
    }

    // if (typeof date === "string" && date.endsWith("Z")) {
    //   return moment(date).utcOffset(-5).format("MMM DD, YYYY hh:mm A");
    // } else {
    return moment(date).format("MMM DD, YYYY hh:mm A");
    // }
};

export const format_date_withZone = (date, apiFormat = false) => {
    if (!date || !moment(date).isValid()) {
        return "-";
    }

    if (apiFormat) {
        return moment.parseZone(date).format("YYYY-MM-DD");
    }
    return moment.parseZone(date).format("MMM DD, YYYY");
};

export const format_dateToEST = (date) => {
    if (!date || !moment(date).isValid()) {
        return "-";
    }

    return moment.utc(date).utcOffset(-5).format("MMM DD, YYYY");
};

export const format_dateTimeToEST = (date) => {
    if (!date || !moment(date).isValid()) {
        return "-";
    }

    return moment.utc(date).utcOffset(-5).format("MMM DD, YYYY hh:mm A");
};

export const truncateDecimal = (value, number = 2) => {
    if (!value) {
        return 0;
    }
    if (isNaN(value)) {
        return value;
    }

    return value.toFixed(number);
};

export const getOperatingSystemDetails = () => {
    let operatingSystem = "Unknown";
    const { appVersion = "" } = window.navigator || {};

    if (appVersion.includes("Win")) {
        operatingSystem = "Windows";
    } else if (appVersion.includes("Mac")) {
        operatingSystem = "Mac";
    } else if (appVersion.includes("X11")) {
        operatingSystem = "UNIX";
    } else if (appVersion.includes("Android")) {
        operatingSystem = "Android";
    } else if (appVersion.includes("IPhone")) {
        operatingSystem = "IPhone";
    } else if (appVersion.includes("Linux")) {
        operatingSystem = "Linux";
    } else {
        operatingSystem = "Unknown";
    }

    return { operatingSystem, osVersion: appVersion?.split(" (")?.[0] };
};

export const getBrowserDetails = () => {
    let currentBrowser = "Unknown";
    let browserVersion = "1.0.0";
    const { userAgent = "" } = window.navigator || {};

    if (userAgent.includes("Chrome")) {
        currentBrowser = "Google Chrome";
        browserVersion = userAgent.split("Chrome/")[1].split(" ")[0];
    } else if (userAgent.includes("Firefox")) {
        currentBrowser = "Mozilla Firefox";
        browserVersion = userAgent.split("Firefox/")[1].split(" ")[0];
    } else if (userAgent.includes("MSIE")) {
        currentBrowser = "Internet Exployer";
        browserVersion = userAgent.split("MSIE/")[1].split(" ")[0];
    } else if (userAgent.includes("Edge")) {
        currentBrowser = "Edge";
        browserVersion = userAgent.split("Edge/")[1].split(" ")[0];
    } else if (userAgent.includes("Safari")) {
        currentBrowser = "Safari";
        browserVersion = userAgent.split("Safari/")[1].split(" ")[0];
    } else if (userAgent.includes("Opera")) {
        currentBrowser = "Opera";
        browserVersion = userAgent.split("Opera/")[1].split(" ")[0];
    } else {
        currentBrowser = "Unknown";
        browserVersion = "1.0.0";
    }

    return { currentBrowser, browserVersion };
};

export const isArray = (array) => {
    if (array && Array.isArray(array)) {
        return array;
    }
    return [];
};

export const parseCamelCase = (text) => {
    if (!text || typeof text !== "string") {
        return "";
    }

    const parsedText = text.replace(/([A-Z])/g, " $1");
    return parsedText.charAt(0).toUpperCase() + parsedText.slice(1);
};

export const getPassedTime = (date) => {
    if (!date || !moment(date).isValid()) {
        return "";
    }

    const passedTime = moment(date).fromNow();
    return passedTime;
};

export const formatNumber = (num, decimalCount = 0) => {
    if (!num) {
        return "";
    }

    let formattedNumber = num?.toString()?.replace(/[^\d.]/g, "");

    if (decimalCount) {
        if (formattedNumber.includes(".")) {
            const parts = formattedNumber.split(".");
            const [integerPart, decimalPart] = parts;

            formattedNumber =
                decimalPart?.length > decimalCount || parts.length >= 1
                    ? `${integerPart}.${decimalPart.slice(0, decimalCount)}`
                    : formattedNumber; // Keep as is if <= decimalCount decimal digits
        }

        return formattedNumber;
    } else {
        return Number(formattedNumber);
    }
};

/** Options for "Discount on cart or specific products/category/brand" then rule. */
export const cartProductOptions = [
    { title: "Cart", highLightText: "Cart", value: "cart" },
    { title: "Specific product", highLightText: "Specific product", value: "specific_product" },
    { title: "Product(s)", highLightText: "Product(s)", value: "products" },
    { title: "Category(ies)", highLightText: "Category(ies)", value: "category" },
    { title: "Brand(s)", highLightText: "Brand(s)", value: "brand" },
];

export const rulesStructure = {
    user: {
        ifRules: [
            {
                title: "is a part of",
                value: "group_part",
                highlightText: "",
                options: [
                    {
                        title: "VIP Users",
                        highLightText: "VIP Users",
                        value: "vip"
                    },
                    {
                        title: "Regional Users",
                        highLightText: "Regional Users",
                        value: "regional"
                    }
                ],
                endTitle: "Group",
                type: "list",
                listType: "user_group",
                selectedValue: null,
                operation: "==",
                attributes: "user_group",
            },
            {
                title: "has spent more than",
                value: "spent_more_than",
                highlightText: "X",
                type: "input",
                inputType: "dollar",
                isOpen: false,
                selectedValue: null,
                operation: ">",
                attributes: "cart_total",
                isGroupRule: true,
                // endTitle:'%',
                groupRule: {
                    title: "in the last",
                    endTitle: "Months",
                    value: "last_n_month",
                    highlightText: "N",
                    type: "input",
                    isOpen: false,
                    selectedValue: null,
                },
            },
            {
                title: "has purchased",
                value: "purchased_n_times",
                highlightText: "N",
                type: "input",
                selectedValue: null,
                isGroupRule: true,
                operation: ">=",
                attributes: "cart_count",
                groupRule: {
                    title: "times till",
                    value: "till_date",
                    highlightText: "date",
                    type: "input",
                    isOpen: false,
                    selectedValue: moment().format("YYYY-MM-DD"),
                },
            },
            {
                title: "has not purchased in the last",
                value: "product_has_not_been_pruchased_n_days",
                highlightText: "N",
                type: "input",
                selectedValue: null,
                endTitle: "days",
                operation: "==",
                attributes: "product_has_not_been_pruchased_n_days",
            },
            {
                title: "has abandoned cart with last days",
                value: "Has_abandoned_cart",
                highlightText: "N",
                type: "input",
                selectedValue: null,
                endTitle: "days",
                operation: "==",
                attributes: "cart_count",
            },
            {
                title: "Has Viewed a specific product/category",
                value: "has_viewed_a_specific_product/category",
                highlightText: "M",
                type: "input",
                selectedValue: null,
                endTitle: "  times without purchase",
                operation: "==",
                attributes: "cart_count",
            },
            {
                title: "Has clicked through from a specific marketing campaign",
                value: "",
                highlightText: "",
                type: "list",
                selectedValue: null,
                endTitle: "",
                type: "list",
                listType: "",
                selectedValue: null,
                operation: "==",
                attributes: "",
            },

        ],
        thenRules: [
            {
                title: "Discount of",
                value: "percentage",
                highlightText: "X",
                type: "input",
                selectedValue: null,
                endTitle: "",
                inputType: "percentage",
                isGroupRule: true,
                groupRule: {
                    title: "upto",
                    value: "dollar",
                    highlightText: "value",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    inputType: "dollar",
                    isGroupRule: true,
                    attributes: "upto_discount",
                    groupRule: {
                        title: "on",
                        value: "on_cart_product",
                        highlightText: "Select",
                        type: "select",
                        isOpen: false,
                        selectedValue: null,
                        isGroupRule: true,
                        options: [...cartProductOptions],
                        groupRule: {
                            title: "",
                            value: "product",
                            highlightText: "Select",
                            type: "list",
                            listType: "product",
                            isOpen: false,
                            selectedValue: [],
                        },
                    },
                },
            },
            {
                title: "Discount of fixed",
                value: "fixed_value",
                highlightText: "Value",
                type: "input",
                inputType: "dollar",
                selectedValue: null,
                isGroupRule: true,
                endTitle: "on",
                groupRule: {
                    title: "",
                    value: "on_cart_product",
                    highlightText: "",
                    type: "select",
                    isOpen: false,
                    selectedValue: null,
                    options: [...cartProductOptions],
                    groupRule: {
                        title: "",
                        value: "",
                        highlightText: "Select",
                        type: "list",
                        listType: "product",
                        isOpen: false,
                        selectedValue: [],
                    },
                },
            },
            {
                title: "Offer free gift",
                value: "offer_free_gift",
                highlightText: "Product",
                // options: [
                //   {
                //     title: "SmartOne Appliance Cards (5 Pack)",
                //     value: "smartone_appliance_5_pack"
                //   },
                //   {
                //     title: "SmartOne Furniture Cards (5 Pack)",
                //     value: "smartone_furniture_5_pack"
                //   },
                //   {
                //     title: "SmartOne Lite Cards (10 Pack)",
                //     value: "smartone_lite_10_pack"
                //   }
                // ],
                type: "list",
                selectedValue: {},
                listType: "free_product",
            },
        ],
    },
    product: {
        ifRules: [
            {
                title: "is in",
                value: "selected_category",
                highlightText: "Selected  ",
                type: "list",
                selectedValue: {},
                listType: "category",
                operation: "IN",
                endTitle: "Category",
                attributes: "category_id",
            },
            {
                title: "is a",
                value: "specific_product",
                highlightText: "Specific Product(s)",
                type: "list",
                selectedValue: [],
                listType: "product",
                operation: "IN",
                attributes: "product_id",
            },
            {
                title: "is purchased in quantity greater than",
                value: "quantity_greater_than",
                highlightText: "N",
                type: "input",
                inputType: "",
                selectedValue: null,
                operation: ">",
                attributes: "cart_count",
            },
            {
                title: "has not been purchased in the last",
                value: "not_purchased_last_n_days",
                highlightText: "N",
                type: "input",
                selectedValue: null,
                endTitle: "days",
                operation: "==",
                attributes: "cart_count",
            },
            {
                title: "is added to cart with",
                value: "added_with_another_product",
                highlightText: "product",
                type: "list",
                listType: "product",
                selectedValue: [],
                operation: "==",
                attributes: "cart_count",
            },
        ],
        thenRules: [
            {
                title: "Discount of",
                value: "percentage_self",
                highlightText: "X",
                type: "input",
                selectedValue: null,
                inputType: "percentage",
                isGroupRule: true,
                groupRule: {
                    title: "upto",
                    value: "dollar",
                    highlightText: "value",
                    type: "input",
                    selectedValue: null,
                    inputType: "dollar",
                    // isGroupRule: false,
                    attributes: "upto_discount",
                    endTitle: "on the product itself",
                },
            },
            {
                title: "Add free",
                value: "add_free_product",
                highlightText: "Product",
                type: "list",
                selectedValue: {},
                listType: "free_product",
            },
            {
                title: "Discount of",
                value: "percentage_other",
                highlightText: "Y",
                type: "input",
                selectedValue: null,

                // isGroupRule: true,
                inputType: "percentage",
                isGroupRule: true,
                groupRule: {
                    title: "upto",
                    value: "dollar",
                    highlightText: "value",
                    type: "input",
                    selectedValue: null,
                    inputType: "dollar",
                    isGroupRule: false,
                    attributes: "upto_discount",
                    endTitle: "on products in cart",
                },
                // groupRule: {
                //   title: '',
                //   endTitle: 'Months',
                //   value: 'last_n_month',
                //   highlightText: 'Product',
                //   type: 'list',
                //   listType: 'product',
                //   isOpen: false,
                //   selectedValue: null,
                //   endTitle: 'in cart'
                // }
            },
            // {
            //   title: 'Discount of',
            //   value: 'percentage_other',
            //   highlightText: 'Y',
            //   type: 'input',
            //   selectedValue: null,
            //   endTitle: "% on",
            //   isGroupRule: true,
            //   inputType: 'percentage',
            //   groupRule: {
            //     title: '',
            //     endTitle: 'Months',
            //     value: 'last_n_month',
            //     highlightText: 'Product',
            //     type: 'list',
            //     listType: 'product',
            //     isOpen: false,
            //     selectedValue: null,
            //     endTitle: 'in cart'
            //   }
            // }
        ],
    },
    cart: {
        ifRules: [
            {
                title: "total exceeds",
                value: "total_exceeds",
                highlightText: "X",
                type: "input",
                selectedValue: null,
                inputType: "dollar",
                operation: ">",
                attributes: "cart_total",
            },
            {
                title: "contains more than",
                value: "contains_more_than_n_items",
                highlightText: "N",
                type: "input",
                selectedValue: null,
                endTitle: "items",
                operation: ">",
                attributes: "cart_count",
            },
            {
                title: "contains specific",
                value: "contains_specific_product",
                highlightText: "Product(s)",
                type: "list",
                listType: "product",
                selectedValue: [],
                operation: "==",
                attributes: "product_id",
            },
            {
                title: "does not include specific",
                value: "does_not_include_specific_product",
                highlightText: "Product(s)",
                type: "list",
                listType: "product",
                selectedValue: [],
                operation: "NOT IN",
                attributes: "product_id",
            },
        ],
        thenRules: [
            {
                title: "Discount of",
                value: "percentage_entire_order",
                highlightText: "X",
                type: "input",
                selectedValue: null,
                inputType: "percentage",
                isGroupRule: true,
                groupRule: {
                    title: " upto",
                    value: "dollar",
                    highlightText: "value",
                    type: "input",
                    selectedValue: null,
                    inputType: "dollar",
                    isGroupRule: false,
                    attributes: "upto_discount",
                    endTitle: "on the entire order",
                },
            },
            {
                title: "Offer Flat discount of",
                value: "flat_discount",
                highlightText: "Y",
                type: "input",
                inputType: "dollar",
                selectedValue: null,
                endTitle: "on the entire order ",
            },
            {
                title: "Free",
                value: "free_gift",
                highlightText: "Gift",
                type: "list",
                listType: "free_product",
                selectedValue: {},
            },
        ],
    },
};

export const _rulesStructure = {
    user: {
        userActivity: {
            ifRules: [
                // User Activity: Is part of a specific group – commented out for now
                // {
                //     title: "Is a part of   ",
                //     value: "user_group_part",
                //     highlightText: "specific",
                //     type: "select",
                //     isOpen: false,
                //     selectedValue: null,
                //     isGroupRule: false,
                //     endTitle: "Group",
                //     options: [
                //         { title: "Vip Users", highLightText: "Vip Users", value: "vip_users" },
                //         { title: "Regional Users", highLightText: "Regional Users", value: "regional_users" },
                //         { title: "New Users", highLightText: "New Users", value: "new_users" },
                //         { title: "Returning Customers", highLightText: "Returning Customers", value: "returning_customers" },
                //     ],
                //     attributes: "user_group",
                // },
                {
                    title: "Has spent more than",
                    value: "spent_more_than",
                    highlightText: "X",
                    type: "input",
                    inputType: "dollar",
                    isOpen: false,
                    selectedValue: null,
                    operation: ">",
                    isGroupRule: true,
                    // endTitle:'%',
                    attributes: "total_spend_last_n_months",
                    groupRule: {
                        title: "in the last",
                        endTitle: "Months",
                        value: "last_n_month",
                        highlightText: "N",
                        type: "input",
                        isOpen: false,
                        selectedValue: null,

                    },
                },
                {
                    title: "Has purchased",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    isGroupRule: true,
                    operation: ">=",
                    attributes: "purchased_n_times_till_date",
                    groupRule: {
                        title: "times till ",
                        value: "till_date",
                        highlightText: "date ",
                        type: "input",
                        inputType: "date",
                        isOpen: false,

                        selectedValue: moment().format("YYYY-MM-DD"),
                    },
                },
                {
                    title: "Has not purchased in the last",
                    value: "not_purchased_last_n_days",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "days",
                    operation: ">=",
                    attributes: "not_purchased_last_n_days",
                },
                {
                    title: "Has abandoned cart with last days",
                    value: "has_abandoned_cart_last_n_days",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "days",
                    operation: ">=",
                    attributes: "has_abandoned_cart_last_n_days",
                },
                // { // needs to work here
                //     title: "Has Viewed a specific product/category",
                //     value: "has viewed a specific product/category ",
                //     highlightText: "M",
                //     type: "input",
                //     selectedValue: null,
                //     endTitle: "  times without purchase",
                //     operation: "==",
                //     attributes: "cart_count",
                // },
                // User Activity: Has viewed a specific product/category M times – commented out for now
                // {
                //     title: "Has Viewed a specific",
                //     value: "product",
                //     highlightText: "product",
                //     type: "list",
                //     listType: "free_product",
                //     selectedValue: null,
                //     endTitle: "items",
                //     inputType: "",
                //     isGroupRule: true,
                //     attributes: "product_id",
                //     groupRule: {
                //         title: "",
                //         value: "view_count",
                //         highlightText: "M ",
                //         type: "input",
                //         selectedValue: null,
                //         inputType: "number",
                //         operation: ">=",
                //         isGroupRule: false,
                //         listType: "category",
                //         endTitle: "times without Purchase",
                //     },
                // },
                // User Activity: Has clicked through from a specific marketing campaign – commented out for now
                // {
                //     title: "Has clicked through  from a ",
                //     value: "",
                //     highlightText: "specific",
                //     type: "",
                //     selectedValue: null,
                //     endTitle: " marketing campaign",
                //     type: "list",
                //     isOpen: false,
                //     listType: "",
                //     selectedValue: null,
                //     isGroupRule: false,
                //     options: [
                //         {
                //             title: "Email",
                //             highLightText: "Email",
                //             value: "Email",
                //         },
                //         {
                //             title: "Social Media",
                //             highLightText: "Social Media",
                //             value: "Social Media",
                //         }, {
                //             title: "Paid Ads",
                //             highLightText: "Paid Ads",
                //             value: "Paid Ads",
                //         },
                //     ],
                //     attributes: "",
                // },

                // {
                //     title: "Has clicked through from a",
                //     value: "campaign_source",
                //     highlightText: "specific",
                //     type: "select",
                //     isOpen: false,
                //     selectedValue: null,
                //     endTitle: " marketing campaign",
                //     isGroupRule: false,
                //     attributes: "campaign_source",
                //     options: [
                //         { title: "Email", highLightText: "Email", value: "email" },
                //         { title: "Social Media", highLightText: "Social Media", value: "social_media" },
                //         { title: "Paid Ads", highLightText: "Paid Ads", value: "paid_ads" },
                //     ],
                // },
                // {
                //     title: "Has searched for",
                //     value: "has_searched_for_specific_keyword",
                //     highlightText: "specific",
                //     type: "input",
                //     inputType: "userSearch",
                //     selectedValue: null,
                //     endTitle: "keywords or terms on the website",
                //     operation: "==",
                //     attributes: "has_searched_for_specific_keyword",
                // },
                {
                    title: "Has purchased from",
                    value: "purchased_from_brand",
                    highlightText: "X",
                    type: "list",
                    listType: "brand",
                    operation: "==",
                    selectedValue: null,
                    endTitle: "brand",
                    attributes: "purchased_from_brand",
                },
                {
                    title: "Has placed their ",
                    value: "first",
                    highlightText: "first",
                    type: "one-level",
                    selectedValue: "first",
                    endTitle: " purchase",
                    operation: "==",
                    attributes: "first_purchase",
                },
                {
                    title: "Has placed their ",
                    value: "second",
                    highlightText: "second",
                    type: "one-level",
                    selectedValue: "second",
                    endTitle: " purchase",
                    operation: "==",
                    attributes: "second_purchase",
                },
                {
                    title: "Has placed their ",
                    value: "third",
                    highlightText: "third",
                    type: "one-level",
                    selectedValue: "third",
                    endTitle: " purchase",
                    operation: "==",
                    attributes: "third_purchase",
                },
                {
                    title: "Has used discount ",
                    value: "used_discount_x",
                    highlightText: "X",
                    type: "input",
                    inputType: "number",
                    endTitle: "",
                    selectedValue: null,
                    operation: "==",
                    attributes: "used_discount_x",
                }

            ],
        },
        loyaltyEngagement: {
            ifRules: [
                {
                    title: "Has been a member for more than  ",
                    value: "membership_duration_months",
                    highlightText: "X",
                    type: "input",
                    selectedValue: null,
                    endTitle: "Months",
                    operation: ">",
                    attributes: "membership_duration_months",
                },
                // Loyalty: Has referred N new customers – commented out for now
                // {
                //     title: "Has refrerrd  ",
                //     value: "referral_count",
                //     highlightText: "N",
                //     type: "input",
                //     selectedValue: null,
                //     endTitle: "new customers",
                //     operation: "==",
                //     attributes: "referral_count",
                // },
                // {
                //     title: "Has reached a specific loyality tier",
                //     value: "has reached a specific loyality tier",
                //     highlightText: "",
                //     type: "select",
                //     selectedValue: null,
                //     endTitle: "",
                //     operation: "==",
                //     attributes: "cart_count",
                // },
                // Loyalty: Has reached a specific loyalty tier – commented out for now
                // {
                //     title: "Has reached a ",
                //     value: "loyalty_tier",
                //     highlightText: "specific",
                //     type: "select",
                //     isOpen: false,
                //     selectedValue: null,
                //     isGroupRule: false,
                //     endTitle: " loyality tier",
                //     options: [{ title: "Gold Member", highLightText: "Gold Member", value: "gold_member" }],
                //     attributes: "loyalty_tier",
                // },
                {
                    title: "Has left a",
                    value: "review_count",
                    highlightText: "product",
                    type: "list",
                    endTitle: "on social media",
                    selectedValue: "",
                    listType: "free_product",
                    endTitle: "review count",
                    attributes: "review_count",
                },

                // {
                //     title: "Has liked or followed the ",
                //     value: "brand name",
                //     highlightText: "brand ",
                //     type: "select",
                //     isOpen: false,
                //     selectedValue: null,
                //     isGroupRule: false,
                //     endTitle: "on social media",
                //     options: [
                //         {
                //             title: "Puma",
                //             highLightText: "Puma",
                //             value: "Puma"
                //         }
                //     ],
                //     attributes: "",

                // },
                {
                    title: "Has Subscribed to a news letter",
                    value: "newsletter_subscribed",
                    highlightText: "",
                    type: "select",
                    selectedValue: true,
                    endTitle: "",
                    operation: "==",
                    attributes: "newsletter_subscribed",
                },
                // {
                //     title: "Has shared    ",
                //     value: "free gift with purchase",
                //     highlightText: "product",

                //     type: "list",
                //     endTitle: "on social media",
                //     selectedValue: "",
                //     listType: "free_product",
                // },

            ],
        },
        demographicsPrefrences: {
            ifRules: [
                {
                    title: "Is within an age range of",
                    value: "age_range_min",
                    highlightText: "min",
                    type: "input",
                    inputType: "age",
                    selectedValue: null,
                    // endTitle: "",
                    operation: ">=",
                    isGroupRule: true,
                    attributes: "age_range",
                    groupRule: {
                        title: "and",
                        value: "age_range_max",
                        highlightText: "max",
                        type: "input",
                        inputType: "age",
                        selectedValue: null,
                        operation: "<=",

                    }
                },
                {
                    title: "Is located in a",
                    value: "specific_region_and_countries",
                    highlightText: "specific region",
                    type: "list",
                    listType: "region",
                    inputType: "select",
                    selectedValue: null,
                    endTitle: " or a country",
                    operation: "==",
                    attributes: "specific_region_and_countries",
                },
                // Demographics: Has shown interest in specific product categories or brands – commented out for now
                // {
                //     title: "Has shown interest in specific product, categories or brands",
                //     value: "interested_brands_and_categories",
                //     highlightText: "",
                //     selectedValue: null,
                //     endTitle: "",
                //     operation: "==",
                //     attributes: "interested_brands_and_categories",
                // },

            ],
            thenRules: []
        },
        specialOccasions: {
            ifRules: [
                {
                    title: "Has an upcoming birthday",
                    value: "upcoming_birthday",
                    highlightText: "",
                    type: "one-level",
                    selectedValue: true,
                    endTitle: "",
                    operation: "==",
                    attributes: "upcoming_birthday",
                }

            ]

        },
        thenRules: [
            {
                title: "Discount of",
                value: "percentage",
                highlightText: "X",
                type: "input",
                selectedValue: null,
                endTitle: "",
                inputType: "percentage",
                isGroupRule: true,
                groupRule: {
                    title: "upto",
                    value: "dollar",
                    highlightText: "value",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    inputType: "dollar",
                    isGroupRule: true,
                    attributes: "upto_discount",
                    groupRule: {
                        title: "on",
                        value: "on_cart_product",
                        highlightText: "Select",
                        type: "select",
                        isOpen: false,
                        selectedValue: null,
                        isGroupRule: true,
                        options: cartProductOptions,
                        groupRule: {
                            title: "",
                            value: "product",
                            highlightText: "Select",
                            type: "list",
                            listType: "product",
                            isOpen: false,
                            selectedValue: [],
                        },
                    },
                },
            },
            // {
            //     title: "Offer free  ",
            //     value: "free gift with purchase",
            //     highlightText: "gift",
            //     type: "list",
            //     endTitle: "with purchase",
            //     selectedValue: {},
            //     listType: "free_product",
            // },
            {
                title: "Offer a free",
                value: "offer_a_free_gift",
                highlightText: "gift",
                type: "list",
                endTitle: "",
                listType: "free_product",
                selectedValue: null,
                operation: "==",
                attributes: "free_gift",
            },
            {
                title: "Offer",
                value: "offer_free_shipping",
                highlightText: "free",
                endTitle: "shipping",
                type: "select",
                selectedValue: "",
                attributes: "free_shipping",
                options: [
                    {
                        title: "standard",
                        highLightText: "Standard",
                        value: "standard",
                    },
                    {
                        title: "expedited",
                        highLightText: "Expedited",
                        value: "expedited",
                    },
                ],
            },
            {
                title: "Send ",
                value: "Send personalized discount code via email or SMS",
                highlightText: "X",
                type: "input",
                inputType: "userSearch",
                selectedValue: null,
                endTitle: "discount code via email or SMS",
                operation: "",
                attributes: "send_discount_mail",
            },
            // Then: Exclusive access to new products or sales – commented out for now
            // {
            //     title: "Exclusive access to ",
            //     value: "specific_product",
            //     highlightText: "new products or sales",
            //     type: "list",
            //     selectedValue: [],
            //     listType: "product",
            //     operation: "IN",
            //     attributes: "product_id",
            // },
            // {
            //     title: "Enter user into a giveaway contest",
            //     value: "Enter user into a giveaway contest",
            //     highlightText: "",
            //     type: "one-level",
            //     endTitle: "",
            //     operation: "",
            //     attributes: "giveaway_contest",
            //     selectedValue: null,
            // },
            // {
            //     title: "Exclusive access to new products or sales",
            //     value: "exclusive_access_new_products_or_sales",
            //     highlightText: "",
            //     attribute: "",
            //     type: "",
            //     operation: "",
            //     selectedValue: null,
            // },
            // {
            //     title: "Offer a limited-time discount on viewed or abandoned cart products",
            //     value: "offer_limited_discount",
            //     highlightText: "",
            //     attribute: "offer_limited_discount",
            //     type: "",
            //     operation: "",
            //     selectedValue: null,
            // },
            // {
            //     title: "Offer early access to",
            //     value: "offer_early_access",
            //     highlightText: "sales or new arrivals",
            //     type: "list",
            //     selectedValue: [],
            //     listType: "product",
            //     operation: "IN",
            //     attributes: "offer_early_access",
            // },
            // {
            //     title: "Provide a personalized product recommendation or styling service",
            //     value: "",
            //     highlightText: "",
            //     attribute: "personalized_product_recommendation",
            //     type: "",
            //     operation: "",
            //     selectedValue: null,
            // },
            {
                title: "Upgraded shipping to expedited service",
                value: "upgrade_shipping",
                highlightText: "",
                type: "one-level",
                endTitle: "",
                operation: "",
                attributes: "upgrade_shipping",
                selectedValue: "expedited",
            },
            // birthday special
            // {
            //     title: "Send a birthday or anniversary",
            //     value: "product",
            //     highlightText: "",
            //     type: "list",
            //     listType: "free_product",
            //     selectedValue: null,
            //     endTitle: "gift or",
            //     inputType: "",
            //     isGroupRule: true,
            //     groupRule: {
            //         title: "",
            //         value: "",
            //         highlightText: "M ",
            //         inputType: "percentage",
            //         type: "input",
            //         selectedValue: null,
            //         operation: "IN",
            //         isGroupRule: false,
            //         listType: "category",
            //         attributes: "category_id",
            //         endTitle: "discount",
            //     },
            // },
            {
                title: "Send a birthday or anniversary gift on ",
                value: "not_purchased_last_n_days",
                highlightText: "M",
                type: "input",
                inputType: "percentage",
                selectedValue: null,
                endTitle: "discount for",
                operation: "==",
                attributes: "special_occasion_gift",
                isGroupRule: true,
                groupRule: {
                    title: "",
                    value: "product",
                    highlightText: "Select product",
                    type: "list",
                    listType: "free_product",
                    isOpen: false,
                    selectedValue: null,
                    isShowError: false,
                },
            },

        ]
    },
    product: {
        productTypecategories: {
            ifRules: [
                {
                    title: "Is in",
                    value: "selected_category",
                    highlightText: "Selected  ",
                    type: "list",
                    selectedValue: null,
                    listType: "category",
                    operation: "IN",
                    endTitle: "Category",
                    attributes: "category_id",
                },
                {
                    title: "Is a",
                    value: "specific_product",
                    highlightText: "Specific Product(s)",
                    type: "list",
                    selectedValue: [],
                    listType: "product",
                    operation: "IN",
                    attributes: "product_id",
                },
                {
                    title: "Is purchased in quantity greater than",
                    value: "quantity_greater_than",
                    highlightText: "N",
                    type: "input",
                    inputType: "number",
                    selectedValue: null,
                    operation: ">",
                    attributes: "product_quantity_greater_than_n",
                    isGroupRule: true,
                    groupRule: {
                        title: "for",
                        value: "quantity_greater_than_products",
                        highlightText: "Specific Product(s)",
                        type: "list",
                        listType: "product",
                        selectedValue: [],
                        isOpen: false,
                        isShowError: false,
                        operation: "IN",
                        attributes: "product_id",
                    },
                },
                {
                    title: "Has not been purchased in the last",
                    value: "product_has_not_been_pruchased_n_days",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "days",
                    operation: ">=",
                    attributes: "product_has_not_been_pruchased_n_days",
                },

                // {
                //     title: "is a new arrival",
                //     value: "limited edition or exclusive ",
                //     highlightText: " new arrival  ",
                //     type: "list",
                //     selectedValue: null,
                //     listType: "",
                //     endValue: "or season Item",
                //     operation: "IN",
                //     attributes: "category_id",
                // },
                {
                    title: "Is a  ",
                    value: "product_new_arrival",
                    highlightText: "Specific",
                    type: "select",
                    selectedValue: null,
                    // listType: "product",
                    endTitle: "edition",
                    operation: "==",
                    attributes: "product_new_arrival",
                    isGroupRule: true,
                    groupRule: {
                        title: "in last",
                        value: "product_new_arrival_days",
                        highlightText: "N",
                        type: "input",
                        inputType: "number",
                        isOpen: false,
                        selectedValue: "",
                        isShowError: false,
                        endTitle: "days",
                    },
                    options: [
                        {
                            title: "New Arrival",
                            highLightText: " select ",
                            value: "new_arrival",
                        },
                        {
                            title: "Seasonal Item",
                            highLightText: "select",
                            value: "seasonal_item",
                        }
                    ]
                },
                {
                    title: "Is in",
                    // value: "limited edition or exclusive tag",
                    value: "product_tag",
                    // highlightText: " exclusive tag ",
                    type: "select",
                    highlightText: "Specific",
                    selectedValue: null,
                    listType: "product",
                    operation: "==",
                    attributes: "product_tag",
                    endTitle: "tag",
                    options: [
                        {
                            title: "Exclusive item",
                            highLightText: "Exclusive item",
                            value: "exclusive",
                        },
                        {
                            title: "Limited edition ",
                            highLightText: "Limited edition",
                            value: "limited_edition",
                        }
                    ]
                },
                {
                    title: "Has package greater than  ",
                    value: "product_quantity_greater_than_n",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    operation: "",
                    attributes: "product_quantity_greater_than_n",
                },
                {
                    title: "Has a ",
                    value: "trait",
                    highLightText: "Trait",
                    highlightText: "Trait",
                    endTitle: " associated with",
                    type: "one-level",
                    selectedValue: null,
                    selectedSubTraits: [],
                    selectedProducts: [],
                    operation: "IN",
                    attributes: "product_trait",
                },
                {
                    title: "Has a ",
                    value: "strain",
                    highLightText: "Strain",
                    highlightText: "Strain",
                    endTitle: " associated with",
                    type: "one-level",
                    selectedValue: null,
                    selectedProducts: [],
                    operation: "IN",
                    attributes: "product_strain",
                },
                {
                    title: "Has a THC % greater than or equal to ",
                    value: "thc_greater_than",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    operation: ">=",
                    attributes: "product_thc",
                },
                {
                    title: "Has a THC % less than or equal to ",
                    value: "thc_less_than",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    operation: "<=",
                    attributes: "product_thc",
                },
                {
                    title: "Has a quantity greater than or equal to ",
                    value: "quantity_greater_than",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    operation: ">=",
                    attributes: "product_quantity_greater_than_n",
                },
                {
                    title: "Has a quantity less than or equal to ",
                    value: "quantity_less_than",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    operation: "<=",
                    attributes: "product_quantity_less_than_n",
                },
                {
                    title: "Has margin greater than or equal to  ",
                    value: "product_margin",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    operation: ">=",
                    attributes: "product_margin",
                },
                {
                    title: "Has margin less than or equal to  ",
                    value: "product_margin",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    operation: "<=",
                    attributes: "product_margin",
                },
                {
                    title: "Has a ",
                    value: "product_type",
                    highlightText: "product type",
                    options: [
                        {
                            title: "Indica",
                            highLightText: "Indica",
                            value: "indica"
                        },
                        {
                            title: "Sativa",
                            highLightText: "Sativa",
                            value: "sativa"
                        },
                        {
                            title: "Hybrid",
                            highLightText: "Hybrid",
                            value: "hybrid"
                        }
                    ],
                    endTitle: "",
                    type: "select",
                    selectedValue: null,
                    operation: "==",
                    attributes: "product_type",
                },

                {
                    title: "Has a Brand",
                    value: "brand_name",
                    highlightText: "Name",
                    type: "list",
                    isOpen: false,
                    listType: "brand",
                    selectedValue: null,
                    // isGroupRule: false,
                    operation: "==",
                    endTitle: "",
                    // options: [
                    //     {
                    //         title: "Puma",
                    //         highLightText: "Puma",
                    //         value: "Puma"
                    //     }
                    // ],
                    attributes: "product_brand",

                },
                {
                    title: "IF customer purchases 2x of any item ",
                    value: "buy_two_get_one_free",
                    attributes: "buy_two_get_one_free",
                    type: "one-level",

                },
                {
                    title: "IF customers adds a product that has been purchased by 20 other customers the same day",
                    value: "product_bought_by_20_customers_same_day",
                    attributes: "product_bought_by_20_customers_same_day",
                    type: "one-level"
                }


            ]
        },
        purchaseHistory: {
            ifRules: [
                {
                    title: "Has not been purchased in last ",
                    value: "product_has_not_been_pruchased_n_days",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "Days",
                    operation: "==",
                    attributes: "product_has_not_been_pruchased_n_days",
                },
                {
                    title: "Is low in stock below",
                    value: "product_lowstock",
                    highlightText: "X",
                    type: "input",
                    selectedValue: null,
                    endTitle: "units",
                    operation: "<",
                    attributes: "product_lowstock",
                },
                // {
                //     title: "Is over stock with over",
                //     value: "product_overstock",
                //     highlightText: "X",
                //     type: "input",
                //     selectedValue: null,
                //     endTitle: "units",
                //     operation: ">",
                //     attributes: "product_overstock",
                // },
            ]
        },
        productBundling: {
            ifRules: [
                {
                    title: "Is added to cart with",
                    value: "added_with_another_product",
                    highlightText: "product(s)",
                    type: "list",
                    listType: "product",
                    selectedValue: [],
                    operation: "==",
                    attributes: "product_id",
                },
                {
                    title: "Is frequently bought with",
                    value: "frequently_bought_with_another_product",
                    highlightText: "another ",
                    type: "list",
                    listType: "product",
                    selectedValue: null,
                    operation: "==",
                    endTitle: "product",
                    attributes: "product_id",
                },
                {
                    title: "Is a complementary product to",
                    value: "complementary_product",
                    highlightText: "other",
                    type: "list",
                    listType: "product",
                    selecteValue: null,
                    endTitle: "item in the cart",
                    attributes: "product_id",
                    operation: "==",
                }

            ]
        },
        priceMargin: {
            ifRules: [
                {
                    title: "Is above a certain ",
                    value: "is_above_a_certain_price_point",
                    highlightText: "X",
                    type: "input",
                    selectedValue: null,
                    inputType: "dollar",
                    endTitle: "price point",
                    operation: ">",
                    attributes: "product_price",
                },
                {
                    title: "Has a high profit",
                    value: "has_a_high_profit",
                    highlightText: "",
                    type: "one-level",
                    selectedValue: true,
                    endTitle: "",
                    operation: "==",
                    attributes: "product_high_profit",
                },
            ]
        },
        thenRules: [
            {
                title: "Discount of",
                highlightText: "X",
                type: "input",
                value: "percentage_self",
                inputType: "percentage",
                selectedValue: null,
                isGroupRule: true,
                attributes: "percentage_self",
                groupRule: {
                    type: "input",
                    title: " upto",
                    value: "dollar",
                    isOpen: false,
                    endTitle: "on the Product itself",
                    highlightText: "value",
                    selectedValue: null,
                    inputType: "dollar",
                    isGroupRule: false,
                }
            },
            {
                title: "Add free",
                value: "free_product",
                highlightText: "Product",
                type: "list",
                endTitle: "with purchase",
                listType: "free_product",
                selectedValue: {},
                attributes: "free_product"
            },
            // {
            //     title: "Discount of",
            //     value: "another_product",
            //     highlightText: "Y",
            //     type: "input",
            //     inputType: "percentage",
            //     selectedValue: null,
            //     endTitle: "on another product in the cart",
            // },
            {
                title: "Discount of",
                highlightText: "X",
                type: "input",
                value: "percentage_another_product",
                inputType: "percentage",
                selectedValue: null,
                isGroupRule: true,
                attributes: "percentage_other_product",
                groupRule: {
                    type: "input",
                    title: " upto",
                    value: "dollar",
                    isOpen: false,
                    endTitle: "on another product in the cart",
                    highlightText: "value",
                    selectedValue: null,
                    inputType: "dollar",
                    isGroupRule: false,
                }
            },
            {
                title: "Bundle with a high demand ",
                value: "bundle_with_high_demand_product",
                highlightText: "product",
                type: "list",
                endTitle: "at a discounted rate",
                listType: "product",
                selectedValue: null,
                operation: "==",
                attributes: "bundle_with_high_demand_product",
            },
            {
                title: "Initiate a flash sale on overstocked items",
                value: "initiate_a_flash_sale_on_overstocked_items",
                highlightText: "",
                type: "one-level",
                endTitle: "",
                listType: "",
                selectedValue: true,
            },
            {
                title: "Offer a free",
                value: "offer_a_free_gift",
                highlightText: "gift",
                type: "list",
                endTitle: "with purchase",
                listType: "free_product",
                selectedValue: null,
                operation: "==",
                attributes: "free_gift",
            },
            {
                title: "Highlight the product as best seller or limited edition",
                value: "highlight_as_best_seller_or_limited_edition",
                highlightText: "",
                type: "one-level",
                // endTitle:"",
                // listType:"",
                selectedValue: true,
                operation: "==",
                attributes: "highlight_best_seller",
            },
            {
                title: "Create a sense of urgency by displaying low stock levels for the product",
                value: "low_stock_urgency",
                highlightText: "N",
                type: "input",
                inputType: "number",
                endTitle: "units",
                selectedValue: null,
                operation: "<=",
                attributes: "low_stock_urgency",
            }
        ]
    },
    cart: {
        cartTotal: {
            ifRules: [
                {
                    // title: " spend total $  ",
                    title: "Spend more than",
                    // value: " spend total $",
                    value: "total_exceeds",
                    inputType: "dollar",
                    highlightText: "X",
                    type: "input",
                    selectedValue: null,
                    endTitle: "",
                    // operation: "==",
                    operation: ">",
                    // attributes: "cart_count",
                    attributes: "cart_total",
                },
                {
                    title: "Contain More than  ",
                    value: "contain_more_than_n_items",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    endTitle: "Items",
                    operation: ">",
                    attributes: "cart_count",
                },
                {
                    title: "Contain only ",
                    value: "contains_specific_product",
                    highlightText: "Specific Product(s)",
                    type: "list",
                    selectedValue: [],
                    listType: "product",
                    operation: "==",
                    attributes: "contain_specific_product",
                },
                {
                    title: "Does not Include  ",
                    value: "does_not_include",
                    highlightText: "Specific Product(s)",
                    type: "list",
                    selectedValue: [],
                    listType: "product",
                    operation: "IN",
                    attributes: "not_contain_specific_product",
                },



            ]
        },
        discount: {
            ifRules: [
                {
                    title: "Has atleast",
                    value: "selected_category",
                    highlightText: "N",
                    type: "input",
                    selectedValue: null,
                    // endTitle: "",
                    // inputType: "",
                    isGroupRule: true,
                    attributes: "specific_category_id",
                    groupRule: {
                        title: "items from a specific",
                        value: "",
                        highlightText: "Category ",
                        type: "list",
                        selectedValue: null,
                        inputType: "",
                        operation: "IN",
                        isGroupRule: false,
                        listType: "category",
                        endTitle: " ",
                    },
                },

                {
                    title: "Has atleast   ",
                    value: "has_atleast_quantity",
                    highlightText: "X",
                    type: "input",
                    selectedValue: null,
                    endTitle: "Quantity for same item",
                    operation: ">=",
                    attributes: "cart_product_same_item_quantity",
                },


                {
                    title: "Is eligible for buy-one get-one ",
                    value: "is_eligible_for_buy-one_get-one",
                    highlightText: "deal",
                    type: "select",
                    isOpen: false,
                    selectedValue: null,
                    isGroupRule: false,
                    endTitle: "",
                    options: [
                        {
                            title: "BOGO",
                            highLightText: "BOGO",
                            value: "BOGO",
                        },

                    ],
                    attributes: "eligible_buy_one_get_one_free",

                },


            ]
        },
        shipping: {
            ifRules: [
                {
                    title: "Is Eligible for ",
                    value: "is_eligible_for_free_shipping",
                    highlightText: "free",
                    type: "select",
                    selectedValue: null,
                    endTitle: "shipping",
                    operation: "==",
                    attributes: "eligible_free_shipping",
                    selectedValue: "Free",
                    exclude: ["stilo", "hyperwolf"],
                    options: [
                        {
                            title: "Standard",
                            highLightText: "Standard",
                            value: "standard",
                        },
                        {
                            title: "Expedited",
                            highLightText: "Expedited",
                            value: "expedited",
                        },
                    ],
                },
                {
                    title: "Uses a  ",
                    value: "Uses_a",
                    highlightText: "specific",
                    type: "select",
                    isOpen: false,
                    selectedValue: null,
                    isGroupRule: false,
                    endTitle: "Payment method",
                    options: [
                        {
                            title: "Wallet",
                            highLightText: "Wallet",
                            value: "Wallet",
                        },
                        {
                            title: "Card",
                            highLightText: "Card",
                            value: "Card",
                        },

                    ],
                    attributes: "payment_method",
                },
            ]
        },
        thenRules: [
            {
                title: "Discount of",
                // value: "flat_discount",
                value: "percentage_entire_order",
                highlightText: "X",
                type: "input",
                inputType: "percentage",
                selectedValue: null,
                endTitle: "",
                isGroupRule: true,
                attributes: "upto_discount",
                groupRule: {
                    title: "on the entire order upto",
                    value: "dollar",
                    highlightText: "value",
                    type: "input",
                    selectedValue: null,
                    inputType: "dollar",
                    isGroupRule: false,
                    endTitle: " ",
                }
            },
            {
                title: "Offer Flat discount of",
                // value: "",
                value: "flat_discount",
                highlightText: "Y",
                type: "input",
                inputType: "dollar",
                selectedValue: null,
                endTitle: "",
                endTitle: "on the entire order "
            },
            {
                title: "Free",
                value: "free_gift",
                highlightText: "Gift",
                type: "list",
                endTitle: "with purchase",
                listType: "free_product",
                selectedValue: {},
            },
            {
                title: "Apply an automatic BOGO for ",
                value: "bogo_deal",
                highlightText: "deal",
                type: "list",
                endTitle: "",
                listType: "free_bogo_product",
                selectedValue: {},
            },

            {
                title: "Offer mystery discount from",
                value: "offer_mystery_discount_at_checkout",
                highlightText: "min",
                type: "input",
                inputType: "percentage",
                selectedValue: null,
                isGroupRule: true,
                endTitle: "",
                attributes: "offer_mystery_discount_at_checkout",
                groupRule: {
                    title: "to",
                    value: "mystery_discount_max_percent",
                    highlightText: "max",
                    type: "input",
                    inputType: "percentage",
                    selectedValue: null,
                    isGroupRule: false,
                    endTitle: "at checkout",
                },
            },

            {
                title: "Offer",
                value: "offer_free_shipping",
                highlightText: "free",
                // type: "one-level",
                endTitle: "shipping",
                // listType: "list",
                type: "select",
                selectedValue: "",
                exclude: ["stilo", "hyperwolf"],
                options: [
                    {
                        title: "standard",
                        highLightText: "Standard",
                        value: "standard",
                    },
                    {
                        title: "expedited",
                        highLightText: "Expedited",
                        value: "expedited",
                    },
                ],
            },

            {
                title: "Spend",
                value: "percentage_self",
                highlightText: "X",
                type: "input",
                selectedValue: null,
                inputType: "dollar",
                isGroupRule: true,
                attributes: "spend_x_get_x",
                groupRule: {
                    title: "more to get",
                    value: "percentage",
                    highlightText: "X ",
                    type: "input",
                    selectedValue: null,
                    inputType: "percentage",
                    // isGroupRule: false,
                    endTitle: " off",
                },
            },
            {
                title: "Suggest related products items based on ",
                value: "Suggest_related_products_items_based_on_cart_contents",
                type: "list",
                highlightText: "cart",
                selectedValue: [],
                endTitle: "contents",
                listType: "product",
                operation: "IN",
                attributes: "product_id",
            },
        ],

    },
    time: {
        time: {
            ifRules: [
                {
                    title: "Is during a",
                    value: "Is_during_a",
                    highlightText: "specific",
                    type: "date_range",
                    isOpen: false,
                    selectedValue: null,
                    isGroupRule: false,
                    endTitle: "period",
                    attributes: "specific_period",
                },
                {
                    title: "Is on",
                    value: "Is_on_specific_days_of_the_week_or_times_of_the_week",
                    highlightText: "specific days",
                    type: "list",
                    listType: "weekdays",
                    isOpen: false,
                    selectedValue: [],
                    isGroupRule: false,
                    endTitle: " of the week",
                    attributes: "specific_day",
                },
                {
                    title: "Is within a limited-time flash sale window ",
                    value: "Is_within_a_limited-time_flash_sale_window",
                    highlightText: "",
                    type: "date_range",
                    selectedValue: null,
                    endTitle: "",
                    // operation: "==",
                    attributes: "flash_sale",
                },



                {
                    title: "Is a pre-order or launch period for a",
                    value: "specific_product",
                    highlightText: "new",
                    type: "list",
                    endTitle: "product",
                    selectedValue: null,
                    listType: "product",
                    operation: "IN",
                    attributes: "product_id",
                },

            ]
        },
        thenRules: [
            {
                title: "Discount of ",
                value: "flat_discount",
                highlightText: "X",
                type: "input",
                inputType: "percentage",
                selectedValue: null,
                endTitle: " during the specified time frame",
                isGroupRule: false,
                attributes: "category_id",
            },
            {
                title: "Apply flash sale discount for",
                value: "flash_sale_discount",
                highlightText: "X",
                type: "input",
                inputType: "number",
                selectedValue: null,
                endTitle: "hours ",
                attributes: "flash_sale_discount",
                isGroupRule: true,
                groupRule: {
                    title: "from ",
                    value: "flash_sale_start_time",
                    highlightText: "HH:MM",
                    type: "input",
                    inputType: "time",
                    selectedValue: "",
                    isOpen: false,
                    isShowError: false,
                    isGroupRule: true,
                    groupRule: {
                        title: "to ",
                        value: "flash_sale_end_time",
                        highlightText: "HH:MM",
                        type: "input",
                        inputType: "time",
                        selectedValue: "",
                        isOpen: false,
                        isShowError: false,
                        isGroupRule: true,
                    },
                },
            },
            {
                title: "Offer free shipping during off-peak hours or specific periods",
                value: "free_shipping_off_peak_hours",
                highlightText: "",
                endTitle: "",
                type: "one-level",
                selectedValue: true,
                attributes: "free_shipping_off_peak_hours",
            },
            {
                title: "Create a sense of urgency with a countdown timer",
                value: "countdown_timer",
                highlightText: "",
                endTitle: "",
                type: "one-level",
                selectedValue: true,
                attributes: "countdown_timer",
            },
            {
                title: "Offer early access or   ",
                value: "Offer_early_access_or_preorder_discount",
                highlightText: "X",
                type: "input",
                inputType: "percentage",
                selectedValue: null,
                endTitle: "discount",
            },
        ]
    },
    payment: {
        paymentShipping: {
            ifRules: [
                {
                    title: "Is a",
                    value: "is_a_specific_type",
                    highlightText: "specific type",
                    type: "select",
                    isOpen: false,
                    selectedValue: null,
                    isGroupRule: false,
                    endTitle: "",
                    options: [
                        {
                            title: "Standard",
                            highLightText: "Standard",
                            value: "Standard",
                        },
                        {
                            title: "Express",
                            highLightText: "Express",
                            value: "Express",
                        },
                        {
                            title: "In-Store Pickup",
                            highLightText: "In-Store Pickup",
                            value: "In-Store Pickup",
                        },
                    ],
                    attributes: "",
                },
            ],
        },
        thenRules: [
            {
                title: "Discount of",
                // value: "flat_discount",
                value: "percentage_entire_order",
                highlightText: "X",
                type: "input",
                inputType: "percentage",
                selectedValue: null,
                endTitle: "",
                isGroupRule: true,
                groupRule: {
                    title: "on the entire order upto",
                    value: "dollar",
                    highlightText: "value",
                    type: "input",
                    selectedValue: null,
                    inputType: "dollar",
                    isGroupRule: false,
                    attributes: "upto_discount",
                    endTitle: " ",
                }
            },
            {
                title: "Offer Flat discount of",
                // value: "",
                value: "flat_discount",
                highlightText: "Y",
                type: "input",
                inputType: "dollar",
                selectedValue: null,
                endTitle: "",
                endTitle: "on the entire order "
            },
        ],
    },
    BOGO: {
        bogo: {
            ifRules: [
                {
                    title: "Buy each",
                    value: "buy_each_x_products",
                    highlightText: "X",
                    type: "input",
                    inputType: "number",
                    selectedValue: null,
                    endTitle: "of",
                    isGroupRule: true,
                    attributes: "bogo_buy_quantity",
                    groupRule: {
                        title: "",
                        value: "product_selection",
                        highlightText: "Specific Product(s)",
                        type: "list",
                        listType: "product",
                        selectedValue: [],
                        attributes: "buy_product_id",
                    }
                },
                {
                    title: "Buy any",
                    value: "buy_any_x_products",
                    highlightText: "X",
                    type: "input",
                    inputType: "number",
                    selectedValue: null,
                    endTitle: "from",
                    isGroupRule: true,
                    attributes: "bogo_buy_quantity",
                    groupRule: {
                        title: "",
                        value: "product_selection",
                        highlightText: "Specific Product(s)",
                        type: "list",
                        listType: "product",
                        selectedValue: [],
                        attributes: "buy_product_id",
                    }
                },
                {
                    title: "Buy each",
                    value: "buy_each_x_categories",
                    highlightText: "X",
                    type: "input",
                    inputType: "number",
                    selectedValue: null,
                    endTitle: "from",
                    isGroupRule: true,
                    attributes: "bogo_buy_quantity",
                    groupRule: {
                        title: "",
                        value: "category_selection",
                        highlightText: "Specific Category(ies)",
                        type: "list",
                        listType: "category",
                        selectedValue: [],
                        attributes: "buy_category_id",
                    }
                },
                {
                    title: "Buy any",
                    value: "buy_any_x_categories",
                    highlightText: "X",
                    type: "input",
                    inputType: "number",
                    selectedValue: null,
                    endTitle: "from",
                    isGroupRule: true,
                    attributes: "bogo_buy_quantity",
                    groupRule: {
                        title: "",
                        value: "category_selection",
                        highlightText: "Specific Category(ies)",
                        type: "list",
                        listType: "category",
                        selectedValue: [],
                        attributes: "buy_category_id",
                    }
                },
                {
                    title: "Buy any",
                    value: "buy_any_x_items_cart",
                    highlightText: "X",
                    type: "input",
                    inputType: "number",
                    selectedValue: null,
                    endTitle: "items from entire cart",
                    attributes: "bogo_buy_quantity_cart",
                }
            ],
            thenRules: [
                {
                    title: "Get",
                    value: "get_x_products",
                    highlightText: "Y",
                    type: "input",
                    inputType: "number",
                    selectedValue: null,
                    endTitle: "of",
                    isGroupRule: true,
                    attributes: "bogo_get_quantity",
                    groupRule: {
                        title: "",
                        value: "product_selection",
                        highlightText: "Product(s)",
                        type: "list",
                        listType: "free_product",
                        selectedValue: [],
                        isGroupRule: false,
                        attributes: "get_product_id",
                    }
                },
                {
                    title: "Get",
                    value: "get_x_categories",
                    highlightText: "Y",
                    type: "input",
                    inputType: "number",
                    selectedValue: null,
                    endTitle: "from",
                    isGroupRule: true,
                    attributes: "bogo_get_quantity",
                    groupRule: {
                        title: "",
                        value: "category_selection",
                        highlightText: "Category(ies)",
                        type: "list",
                        listType: "category",
                        selectedValue: [],
                        isGroupRule: false,
                        attributes: "get_category_id",
                    }
                },
                {
                    title: "Get",
                    value: "get_x_cheapest",
                    highlightText: "Y",
                    type: "input",
                    inputType: "number",
                    selectedValue: null,
                    endTitle: "cheapest items free",
                    isGroupRule: false,
                    attributes: "bogo_get_quantity",
                },
            ],
        },
    },
}



export const promotionCreteria = {
    user: [
        { id: "userActivity", value: "User Activity & Purchase History" },
        { id: "loyaltyEngagement", value: "Loyalty & Engagement" },
        { id: "demographicsPrefrences", value: "Demographics and Preferences" },
        { id: "specialOccasions", value: "Special Occasions" },
    ],
    product: [
        { id: "productTypecategories", value: "Product Type & Category" },
        { id: "purchaseHistory", value: "Purchase History & Inventory" },
        { id: "productBundling", value: "Product Bundling & Upselling" },
        { id: "priceMargin", value: "Price and Margin" }


    ],
    cart: [
        { id: "cartTotal", value: "Cart Total & Items" },
        { id: "discount", value: "Discount & Upselling Opportunities" },
        { id: "shipping", value: "Shipping and Payment" },


    ],
    payment: [
        { id: "paymentShipping", value: "Payment & Shipping" }
    ],
    time: [
        {
            id: "time", value: "Time promotions", //here is not subcategory
        }
    ],
    BOGO: [
        { id: "bogo", value: "BOGO Deals" }
    ]


}








export const sanitizeAgeInput = (value) => {
    if (!value) return "";
    const digits = value.replace(/[^0-9]/g, "");
    if (!digits) return "";
    return String(Number(digits)); // Removes leading zeros
};

/** Clamp 0–100 for mystery discount min/max percent fields. */
export const sanitizePercentInput = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const cleaned = String(value).replace(/[^0-9.]/g, "");
    if (cleaned === "") return "";
    const n = parseFloat(cleaned);
    if (Number.isNaN(n)) return "";
    const clamped = Math.min(100, Math.max(0, n));
    return String(clamped);
};

/** Then-rule: mystery discount at checkout (min % on parent, max % on groupRule). Supports legacy `value` id. */
export const isOfferMysteryDiscountAtCheckoutThenRule = (rule) => {
    if (!rule?.groupRule) return false;
    const isMain =
        rule.value === "offer_mystery_discount_at_checkout" ||
        rule.value === "mystery_discount_min_percent";
    return isMain && rule.groupRule.value === "mystery_discount_max_percent";
};

export const updateRuleValue = ({
    rules,
    ruleValue,
    ruleId,
    isGroupRule = false,
}) => {
    return rules.map((rule) => {
        // If this is the target rule
        if (rule.value === ruleId) {
            if (isGroupRule && rule.groupRule) {
                // Update the groupRule's selectedValue
                let updatedRule = {
                    ...rule,
                    groupRule: {
                        ...rule.groupRule,
                        selectedValue: ruleValue,
                        isShowError: false,
                    },
                };

                console.log(rule, "rruulleee")

                // Special handling for age range: ensure max >= min
                if (rule.value === "age_range_min" && rule.groupRule.value === "age_range_max") {
                    // Sanitize if it's the value we are updating (ruleValue corresponds to groupRule i.e. Max)
                    let maxVal = sanitizeAgeInput(ruleValue);
                    updatedRule.groupRule.selectedValue = maxVal; // Update with sanitized value

                    const min = Number(rule.selectedValue);
                    let max = Number(maxVal);

                    if (!isNaN(min) && !isNaN(max)) {
                        const isRangeInvalid = max < min;
                        const isMinInvalid = min < 18 || min > 100;
                        const isMaxInvalid = max > 100;

                        updatedRule = {
                            ...updatedRule,
                            isShowError: isMinInvalid || isRangeInvalid,
                            groupRule: {
                                ...updatedRule.groupRule,
                                isShowError: isMaxInvalid || isRangeInvalid,
                            },
                        };
                    }
                }

                return updatedRule;
            } else {
                // Update the main rule's selectedValue
                let updatedRule = {
                    ...rule,
                    selectedValue: ruleValue,
                    isShowError: false,
                };

                // Mystery discount at checkout: min % (main) vs max % (groupRule)
                if (isOfferMysteryDiscountAtCheckoutThenRule(rule)) {
                    const minVal = sanitizePercentInput(ruleValue);
                    updatedRule.selectedValue = minVal;
                    const min = minVal === "" ? NaN : Number(minVal);
                    const max =
                        rule.groupRule.selectedValue === "" || rule.groupRule.selectedValue == null
                            ? NaN
                            : Number(rule.groupRule.selectedValue);
                    if (!Number.isNaN(min) && !Number.isNaN(max)) {
                        const isRangeInvalid = max < min;
                        const isMinInvalid = min < 0 || min > 100;
                        const isMaxInvalid = max < 0 || max > 100;
                        // Only flag the field being edited for range: min too high vs max is a min error.
                        updatedRule = {
                            ...updatedRule,
                            isShowError: isMinInvalid || isRangeInvalid,
                            groupRule: {
                                ...updatedRule.groupRule,
                                isShowError: isMaxInvalid,
                            },
                        };
                    }
                    return updatedRule;
                }

                // Special handling for age range: ensure max >= min
                if (
                    rule.value === "age_range_min" &&
                    rule.groupRule &&
                    rule.groupRule.value === "age_range_max"
                ) {
                    // Sanitize if it's the value we are updating (ruleValue corresponds to main rule i.e. Min)
                    let minVal = sanitizeAgeInput(ruleValue);
                    updatedRule.selectedValue = minVal; // Update with sanitized value

                    const min = Number(minVal);
                    const max = Number(rule.groupRule.selectedValue);

                    if (!isNaN(min) && !isNaN(max)) {
                        const isRangeInvalid = max < min;
                        const isMinInvalid = min < 18 || min > 100;
                        const isMaxInvalid = max > 100;

                        updatedRule = {
                            ...updatedRule,
                            isShowError: isMinInvalid || isRangeInvalid,
                            groupRule: {
                                ...updatedRule.groupRule,
                                isShowError: isMaxInvalid || isRangeInvalid,
                            },
                        };
                    }
                }

                return updatedRule;
            }
        }
        if (rule.isGroupRule && rule.groupRule && rule.groupRule.value === ruleId) {
            let nextValue = ruleValue;

            // Mystery discount at checkout: max % must be >= min %
            if (isOfferMysteryDiscountAtCheckoutThenRule(rule)) {
                const maxVal = sanitizePercentInput(ruleValue);
                nextValue = maxVal;
                const min = rule.selectedValue === "" || rule.selectedValue == null ? NaN : Number(rule.selectedValue);
                const max = maxVal === "" ? NaN : Number(maxVal);
                if (!Number.isNaN(min) && !Number.isNaN(max)) {
                    const isRangeInvalid = max < min;
                    const isMinInvalid = min < 0 || min > 100;
                    const isMaxInvalid = max < 0 || max > 100;
                    // Only flag max while typing max (e.g. "2" before "20" must not turn min red).
                    return {
                        ...rule,
                        isShowError: isMinInvalid,
                        groupRule: {
                            ...rule.groupRule,
                            selectedValue: maxVal,
                            isShowError: isMaxInvalid || isRangeInvalid,
                        },
                    };
                }
                return {
                    ...rule,
                    isShowError: false,
                    groupRule: {
                        ...rule.groupRule,
                        selectedValue: maxVal,
                        isShowError: false,
                    },
                };
            }

            // Special handling for age range: ensure max >= min
            if (rule.value === "age_range_min" && rule.groupRule.value === "age_range_max") {
                // Sanitize input (Max)
                let maxVal = sanitizeAgeInput(ruleValue);
                nextValue = maxVal; // Update nextValue for the return payload

                const min = Number(rule.selectedValue);
                let max = Number(maxVal);

                if (!isNaN(min) && !isNaN(max)) {
                    const isRangeInvalid = max < min;
                    const isMinInvalid = min < 18 || min > 100;
                    const isMaxInvalid = max > 100;

                    console.log("Max Update Errors:", { isRangeInvalid, isMinInvalid, isMaxInvalid });

                    rule = {
                        ...rule,
                        isShowError: isMinInvalid || isRangeInvalid,
                        groupRule: {
                            ...rule.groupRule,
                            isShowError: isMaxInvalid || isRangeInvalid
                        }
                    }
                }
            } let rulePayload = {};
            if (rule.groupRule.attributes === "upto_discount") {
                rulePayload = {
                    ...rule,
                    groupRule: {
                        ...rule.groupRule,
                        selectedValue: nextValue,
                        upto: nextValue,
                        isShowError: false,
                    },
                };
            } else {
                rulePayload = {
                    ...rule,
                    groupRule: {
                        ...rule.groupRule,
                        selectedValue: nextValue,
                        isShowError: rule.groupRule.isShowError || false,
                    },
                };
            }
            return rulePayload;
        }
        return rule;
    });
};

// export const updateRuleValue = ({
//   rules,
//   ruleValue,
//   ruleId,
//   isGroupRule = false,
// }) => {
//   const recursiveUpdate = (rule) => {
//     // If the current rule matches the ruleId
//     if (rule.value === ruleId) {
//       return {
//         ...rule,
//         selectedValue: ruleValue,
//       };
//     }

//     // If the rule has a nested groupRule, recurse into it
//     if (rule.groupRule) {
//       const updatedGroupRule = recursiveUpdate(rule.groupRule);
//       // Check if the groupRule was updated
//       if (updatedGroupRule !== rule.groupRule) {
//         return {
//           ...rule,
//           groupRule: updatedGroupRule,
//         };
//       }
//     }

//     return rule;
//   };

//   return rules.map(recursiveUpdate);
// };

export const validateSelectedValues = (obj) => {
    // Base case: if not an object or null, return as is
    if (!obj || typeof obj !== "object") {
        return obj;
    }

    // Helper function to check if value is empty

    const isEmpty = (value) => {
        if (value === null || value === undefined) {
            return true;
        }
        if (Array.isArray(value)) {
            return value.length === 0;
        }
        if (typeof value === "string") {
            return value.trim() === "";
        }
        return false;
    };

    // If it's an array, process each element
    if (Array.isArray(obj)) {
        return obj.map((item) => validateSelectedValues(item));
    }

    // Create a new object with processed properties
    const newObj = cloneDeep(obj);

    // Check if this object or any of its nested objects have selectedValue
    // let hasSelectedValueKey = false;
    const checkForSelectedValue = (object) => {
        if (!object || typeof object !== "object") {
            return false;
        }

        // Conditional field: "Is a New Arrival edition" shows date only for new_arrival
        // When not new_arrival, remove groupRule.selectedValue so it won't validate/error.
        if (
            object.value === "product_new_arrival" &&
            object.groupRule &&
            object.selectedValue !== "new_arrival"
        ) {
            if ("selectedValue" in object.groupRule) {
                delete object.groupRule.selectedValue;
            }
            object.groupRule.isShowError = false;
        }

        if ("selectedValue" in object) {
            // hasSelectedValueKey = true;
            // Check if selectedValue is empty and set isShowError accordingly

            const isSelectedObject =
                object.selectedValue !== null && typeof object.selectedValue === "object";
            object.isShowError =
                (object.isGroupRule &&
                    object.groupRule?.selectedValue === "any_product") ||
                    object.selectedValue === "any_product"
                    ? false
                    : isEmpty(object.selectedValue) ||
                    (isSelectedObject && Object.keys(object.selectedValue).length === 0);

            // Flash sale time validation: hours must match time range
            if (
                object.value === "flash_sale_discount" &&
                object.groupRule?.value === "flash_sale_start_time" &&
                object.groupRule?.groupRule?.value === "flash_sale_end_time"
            ) {
                const durationHours = Number(object.selectedValue);
                const start = object.groupRule?.selectedValue;
                const end = object.groupRule?.groupRule?.selectedValue;
                const parseTime = (t) => {
                    if (!t || typeof t !== "string") return null;
                    const [h, m] = t.split(":").map((v) => Number(v));
                    if (Number.isNaN(h) || Number.isNaN(m)) return null;
                    return h * 60 + m;
                };
                const startMin = parseTime(start);
                const endMin = parseTime(end);
                if (startMin != null && endMin != null && !Number.isNaN(durationHours)) {
                    const diffMinutes =
                        endMin >= startMin ? endMin - startMin : 24 * 60 - startMin + endMin;
                    const expectedMinutes = durationHours * 60;
                    const mismatch = diffMinutes !== expectedMinutes || expectedMinutes <= 0;
                    if (mismatch) {
                        object.isShowError = true;
                        if (object.groupRule) object.groupRule.isShowError = true;
                        if (object.groupRule?.groupRule) object.groupRule.groupRule.isShowError = true;
                    }
                }
            }

            // Strict Age Range Validation during Submit/Validate
            if (object.value === "age_range_min" && object.groupRule && object.groupRule.value === "age_range_max") {
                const min = Number(object.selectedValue);
                const max = Number(object.groupRule.selectedValue);
                if (!isNaN(min) && !isNaN(max)) {
                    const isRangeInvalid = max < min;
                    const isMinInvalid = min < 18 || min > 100;
                    const isMaxInvalid = max > 100;

                    object.isShowError = isMinInvalid || isRangeInvalid;
                    if (object.groupRule) {
                        object.groupRule.isShowError = isMaxInvalid || isRangeInvalid;
                    }
                }
            }
            object.selectedValue =
                object.isGroupRule && object.groupRule?.selectedValue === "any_product"
                    ? null
                    : object.selectedValue;
            object.isOpen = false;
        }

        if (Array.isArray(object)) {
            object.forEach((item) => checkForSelectedValue(item));
        } else {
            Object.values(object).forEach((value) => checkForSelectedValue(value));
        }
    };

    checkForSelectedValue(newObj);

    // Recursively process all nested properties
    Object.keys(newObj).forEach((key) => {
        newObj[key] = validateSelectedValues(newObj[key]);
    });

    return newObj;
};

export const format_alpha_numeric = (str) => {
    if (!str || typeof str !== "string") {
        return "";
    }

    return str.replace(/[^a-zA-Z0-9]/g, "");
};

// export function validRuleFields(data) {
//   let isValidOrNot = true; // Default value is true
//   let skipNextGroupValue = false; // Flag to skip the next groupValue object if listType is 'cart'
//   if (
//     data.ifRules.length === 0 ||
//     data.thenRules.length === 0 ||
//     data.ruleType === ""
//   ) {
//     return false;
//   }

//   // Recursive function to check selectedValue in any nested object
//   const checkSelectedValue = (obj) => {
//     // Ensure obj is not null or undefined before accessing properties
//     if (obj === null || obj === undefined) {
//       return true; // If the object is null or undefined, it's valid
//     }

//     // Check if selectedValue is defined in the object
//     if (obj.selectedValue !== undefined) {
//       // if (obj.selectedValue === null && obj.value !== "buy_product") {
//       if (
//         obj.selectedValue === null &&
//         !(
//           (obj.value === "buy_product" || obj.value === "get_one_product") &&
//           obj.isGroupRule &&
//           obj.groupRule.selectedValue === "any_product"
//         )
//       ) {
//         // If it's null, consider it invalid
//         return false;
//       }

//       if (typeof obj.selectedValue === "string") {
//         // If it's a string, check if it's empty
//         if (obj.selectedValue === "") {
//           return false;
//         }
//       } else if (Array.isArray(obj.selectedValue)) {
//         // If it's an array, check if it's empty
//         if (obj.selectedValue.length === 0) {
//           return false;
//         }
//       } else if (typeof obj.selectedValue === "object") {
//         // If it's an object, check if it's empty (no properties)
//         if (
//           obj.selectedValue !== null &&
//           Object.keys(obj.selectedValue).length === 0
//         ) {
//           return false;
//         } else {
//           // If it's a non-empty object, recursively check its properties
//           if (!checkSelectedValue(obj.selectedValue)) {
//             return false;
//           }
//         }
//       }
//     }

//     // If there's a groupRule or any nested object, check inside it as well
//     if (obj.groupRule && !checkSelectedValue(obj.groupRule)) {
//       return false;
//     }

//     // Check other possible nested objects (e.g., within 'thenRules' or other arrays)
//     for (const key in obj) {
//       if (typeof obj[key] === "object") {
//         if (!checkSelectedValue(obj[key])) {
//           return false;
//         }
//       }
//     }

//     return true;
//   };

//   // Loop through the ifRules and thenRules and check selectedValue
//   for (const rulesArray of [data.ifRules, data.thenRules]) {
//     for (const rule of rulesArray) {
//       // Check if any of the groupValue listType is cart and skip the next groupValue object
//       if (rule?.groupRule && rule?.groupRule?.selectedValue === "cart") {
//         skipNextGroupValue = true;
//         isValidOrNot = true;
//         break;
//       }
//       if (!skipNextGroupValue) {
//         if (!checkSelectedValue(rule)) {
//           isValidOrNot = false;
//           break;
//         }
//       } else {
//         skipNextGroupValue = false; // Reset the flag after skipping the next groupValue object
//       }
//     }
//     if (!isValidOrNot) {
//       break;
//     } // Exit the outer loop if invalid
//   }

//   return isValidOrNot;
// }

export function generateConcatString(data) {

    // Function to process selectedValue and return the correct JSX
    const processSelectedValue = (
        selectedValue,
        listType,
        endTitle,
        groupselectedValue,
        inputType
    ) => {

        const prefix = inputType === 'dollar' ? '$' : '';
        const suffix = inputType === 'percentage' ? '%' : '';
        // Handle string type
        if (typeof selectedValue === 'string') {
            return (
                <span>
                    {prefix}
                    <strong>{selectedValue}</strong>
                    {suffix}
                </span>
            );
        }
        // Handle object or array types
        if (typeof selectedValue === 'object' && selectedValue !== null) {
            // Handle array of items (e.g., product list)
            if (Array.isArray(selectedValue) && listType === 'product') {
                return selectedValue.map(item => (
                    <span key={item?.id}>
                        <strong>{item?.display_name} </strong>
                        {endTitle}&nbsp;
                        <strong>{prefix}{groupselectedValue}{suffix}</strong>
                    </span>
                ));
            }

            // Handle array of regions
            if (Array.isArray(selectedValue) && listType === 'region') {
                return selectedValue?.map(item => (
                    <span key={item?.regionId}>
                        <strong>{item?.regionName} </strong>
                    </span>
                ));
            }

            // Handle single object with display_name
            if (selectedValue?.display_name) {
                return (
                    <span key={selectedValue?.id}>
                        <strong>{selectedValue?.display_name} </strong>
                        {endTitle}&nbsp;
                        <strong>{prefix}{groupselectedValue}{suffix}</strong>
                    </span>
                );
            }

            // Handle single object with regionName
            if (selectedValue?.regionName && listType === 'region') {
                return (
                    <span key={selectedValue?.regionId}>
                        <strong>{selectedValue?.regionName} </strong>
                    </span>
                );
            }
        }

        // Handle specific period (date range)
        if (type === 'date_range' && selectedValue?.startDate) {
            const start = moment(selectedValue.startDate).format("MMM DD, YYYY HH:mm");
            const end = selectedValue.endDate ? moment(selectedValue.endDate).format("MMM DD, YYYY HH:mm") : "...";
            return (
                <span key="specific_period">
                    <strong>{start} </strong> to <strong>{end} </strong>
                </span>
            );
        }

        // Default fallback
        return '';
    };

    let result = [];

    // Process the ifRules array
    if (data?.ifRules && data?.ifRules?.length > 0) {
        result.push(<strong key="If">If {data?.ruleType}  </strong>);

        data.ifRules.forEach((rule) => {
            const title = rule.title || '';
            const selectedValue = processSelectedValue(rule?.selectedValue, rule?.listType, rule?.endTitle, rule?.groupRule?.selectedValue,
            );
            const endTitle = rule?.groupRule ? rule?.groupRule.endTitle : rule?.endTitle || '';
            result.push(
                <span key={`ifRule-${title}`}>{title} {selectedValue} {endTitle} </span>
            );
        });
    }
    // Add the word "then" before concatenating the thenRules
    if (data?.thenRules && data?.thenRules?.length > 0) {
        result.push(<strong key="then">Then &nbsp;</strong>);
        // Process the thenRules array
        data.thenRules.forEach((rule) => {
            const title = rule.title || '';
            const selectedValue = processSelectedValue(rule?.selectedValue, rule?.listType, rule?.endTitle, rule?.groupRule?.selectedValue, rule?.isGroupRule ? rule?.groupRule?.inputType : rule?.inputType);
            const endTitle = rule?.groupRule ? rule?.groupRule?.endTitle : rule?.endTitle || '';

            result.push(
                <span key={`thenRule-${title}`}>{title} {selectedValue} {endTitle} </span>
            );
        });
    }
    return <div>{result}</div>;
}

export function validRuleFields(data) {
    let isValidOrNot = true; // Default value is true
    let skipNextGroupValue = false; // Flag to skip the next groupValue object if listType is 'cart'
    if (
        data.ifRules.length === 0 ||
        data.thenRules.length === 0 ||
        data.ruleType === ""
    ) {
        return false;
    }

    // Recursive function to check selectedValue in any nested object
    const checkSelectedValue = (obj) => {
        // Ensure obj is not null or undefined before accessing properties
        if (obj === null || obj === undefined) {
            return true; // If the object is null or undefined, it's valid
        }

        if (obj.isShowError) {
            return false;
        }

        // Check if selectedValue is defined in the object
        if (obj.selectedValue !== undefined) {
            console.log("Checking ValidRule Obj:", obj.value, "Error:", obj.isShowError);
            // Allow null for specific optional fields so "Create rule" popup closes
            if (obj.selectedValue === null || obj.selectedValue === undefined) {
                const allowNull =
                    (obj?.value === "buy_product" || obj?.value === "get_one_product") &&
                    obj?.isGroupRule &&
                    obj?.groupRule?.selectedValue === "any_product";
                // "Discount of X% upto $Y on" – "on" (cart/products) can be unset; treat as entire order
                const allowNullOnCartProduct = obj?.value === "on_cart_product";
                if (!allowNull && !allowNullOnCartProduct) return false;
            }

            if (typeof obj.selectedValue === "string") {
                // If it's a string, check if it's empty
                if (obj.selectedValue === "") {
                    return false;
                }
            } else if (Array.isArray(obj.selectedValue)) {
                // If it's an array, check if it's empty
                if (obj.selectedValue.length === 0) {
                    return false;
                }
            } else if (typeof obj.selectedValue === "object") {
                // If it's an object, check if it's empty (no properties)
                if (
                    obj.selectedValue !== null &&
                    Object.keys(obj.selectedValue).length === 0
                ) {
                    return false;
                } else {
                    // If it's a non-empty object, recursively check its properties
                    if (!checkSelectedValue(obj.selectedValue)) {
                        return false;
                    }
                }
            }
        }

        // If there's a groupRule or any nested object, check inside it as well
        // Skip nested "on" product list when "on" (on_cart_product) is unset – treat as entire order
        const onCartUnset = obj.value === "on_cart_product" && (obj.selectedValue == null);
        const newArrivalDateHidden =
            obj.value === "product_new_arrival" &&
            obj.selectedValue !== "new_arrival";
        if (obj.groupRule && !onCartUnset && !newArrivalDateHidden) {
            if (!checkSelectedValue(obj.groupRule)) {
                return false;
            }
        }

        // Check other possible nested objects (e.g., within 'thenRuReviewes' or other arrays)
        const skipGroupRule = obj.value === "on_cart_product" && (obj.selectedValue == null);
        for (const key in obj) {
            if (key === "groupRule" && skipGroupRule) continue;
            if (key === "groupRule" && (obj.value === "product_new_arrival" && obj.selectedValue !== "new_arrival")) continue;
            if (typeof obj[key] === "object") {
                if (!checkSelectedValue(obj[key])) {
                    return false;
                }
            }
        }

        return true;
    };

    // Loop through the ifRules and thenRules and check selectedValue
    for (const rulesArray of [data.ifRules, data.thenRules]) {
        for (const rule of rulesArray) {
            // Check if any of the groupValue listType is cart and skip the next groupValue object

            if (
                rule?.groupRule?.groupRule &&
                rule?.groupRule?.groupRule?.selectedValue === "cart"
            ) {
                skipNextGroupValue = true;
                isValidOrNot = true;
                break;
            }
            if (!skipNextGroupValue) {
                if (!checkSelectedValue(rule)) {
                    isValidOrNot = false;
                    break;
                }
            } else {
                skipNextGroupValue = false; // Reset the flag after skipping the next groupValue object
            }
        }
        if (!isValidOrNot) {
            break;
        } // Exit the outer loop if invalid
    }

    return isValidOrNot;
}

const keysToExtract = ["title", "endTitle", "selectedValue", "highlightText"];

export function extractSpecificKeys(data) {
    // Helper function to extract keys from a single object
    const extractFromObject = (obj) => {
        if (!obj || typeof obj !== "object") {
            return null;
        }

        const extracted = {};
        keysToExtract.forEach((key) => {
            // Special handling for selectedValue if it's an object
            if (key === "selectedValue" && typeof obj[key] === "object") {
                extracted[key] = obj[key].display_name || obj[key];
            } else {
                extracted[key] = obj[key] || null;
            }
        });
        return extracted;
    };

    const result = [];

    // Process ifRules
    if (data.ifRules && Array.isArray(data.ifRules)) {
        data.ifRules.forEach((rule) => {
            // Extract from main rule
            const mainExtracted = extractFromObject(rule);
            if (mainExtracted) {
                result.push(mainExtracted);
            }

            // Extract from groupRule if exists
            if (rule.groupRule) {
                const groupExtracted = extractFromObject(rule.groupRule);
                if (groupExtracted) {
                    result.push(groupExtracted);
                }
            }
        });
    }

    // Process thenRules
    if (data.thenRules && Array.isArray(data.thenRules)) {
        data.thenRules.forEach((rule) => {
            const extracted = extractFromObject(rule);
            if (extracted) {
                result.push(extracted);
            }
        });
    }

    return result;
}

export function formatValue(value) {
    if (!value) {
        return "";
    }

    let processedValue = value;
    if (typeof value === "string" && (value.startsWith("{") || value.startsWith("["))) {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === "object") {
                processedValue = parsed;
            }
        } catch (e) {
            // Keep as string
        }
    }

    if (typeof processedValue === "string") {
        return processedValue;
    }
    if (Array.isArray(processedValue)) {
        return processedValue
            .map(
                (item) =>
                    item.title ||
                    item.display_name ||
                    item?.name ||
                    item?.brandName ||
                    item?.categoryName ||
                    item?.regionName,
            )
            .join(", ");
    }

    if (typeof processedValue === "object") {
        return (
            processedValue.title ||
            processedValue.display_name ||
            processedValue.name ||
            processedValue.brandName ||
            processedValue.categoryName ||
            processedValue.regionName ||
            (processedValue.startDate ? `${moment(processedValue.startDate).format("MMM DD YYYY HH:mm")} to ${processedValue.endDate ? moment(processedValue.endDate).format("MMM DD YYYY HH:mm") : "..."}` : null) ||
            JSON.stringify(processedValue)
        );
    }
    return String(processedValue);
}

// if (Array.isArray(value)) {
//   if (value.length > 2) {
//     return `${value.slice(0, 2).map((item) => item.display_name || item?.name).join(", ")} and ${value.length - 2} more`;
//   }
//   return value.map((item) => item.display_name || item?.name).join(", ");
// }

// if (typeof value === "object") {
//   return value.display_name || value.name || JSON.stringify(value);
// }
// return String(value);

export function processProductArrayDynamically(val) {
    if (!val) {
        return {
            formattedString: "",
            splitArray: [],
        };
    }

    let splitArray = [];

    // Handle string input
    if (typeof val === "string") {
        const cleanString = val.trim();
        if (cleanString === "") {
            return {
                formattedString: "",
                splitArray: [],
            };
        }

        // Try parsing JSON if it looks like JSON
        if (cleanString.startsWith("{") || cleanString.startsWith("[")) {
            try {
                const parsed = JSON.parse(cleanString);
                if (parsed && typeof parsed === "object") {
                    return processProductArrayDynamically(parsed);
                }
            } catch (e) {
                // Not JSON, continue as normal string
            }
        }

        splitArray = cleanString
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item !== "");
    }
    // Handle array input
    else if (Array.isArray(val)) {
        splitArray = val.map(item => formatValue(item)).filter(Boolean);
    }
    // Handle object input
    else if (typeof val === "object") {
        const formatted = formatValue(val);
        if (formatted) splitArray = [formatted];
    }

    // Create formatted string
    let formattedString = "";
    if (splitArray.length <= 2) {
        formattedString = splitArray.join(", ");
    } else {
        const firstTwo = splitArray.slice(0, 2);
        formattedString = firstTwo.join(", ");
    }

    return {
        formattedString,
        splitArray,
    };
}

/** Resolve category display name: support selectedValue as ID string or { categoryId, categoryName } object. */
function getCategoryDisplayValue(selectedValue, getCategoryById) {
    if (!selectedValue) return "";
    if (Array.isArray(selectedValue)) {
        return selectedValue
            .map((v) => getCategoryDisplayValue(v, getCategoryById))
            .filter(Boolean)
            .join(", ");
    }
    const id = typeof selectedValue === "object" ? selectedValue?.categoryId || selectedValue?.id : selectedValue;
    const fromLookup = getCategoryById(id)?.categoryName || getCategoryById(id)?.name;
    const fromObj = typeof selectedValue === "object" ? selectedValue?.categoryName : null;
    return formatValue(fromLookup || fromObj);
}

/** Resolve user group display name: support selectedValue as ID string or { groupId, groupName } object. */
function getGroupDisplayValue(selectedValue, getGroupById) {
    if (!selectedValue) return "";
    console.log("selectedValue", selectedValue);
    const id = typeof selectedValue === "object" ? selectedValue?.groupId || selectedValue?.id : selectedValue;
    const fromLookup = getGroupById(id)?.title || getGroupById(id)?.name;
    const fromObj = typeof selectedValue === "object" ? selectedValue?.groupName : null;
    return formatValue(fromLookup || fromObj);
}

function formatRulePart(rule, getCategoryById, getGroupById) {
    const isBooleanValue = typeof rule?.selectedValue === "boolean";
    const isOneLevelBoolean = rule?.type === "one-level" && isBooleanValue;
    const isSelectBoolean = rule?.type === "select" && isBooleanValue;
    const getSelectTitle = (node) => {
        if (!node || node.type !== "select" || !Array.isArray(node.options)) return null;
        const opt = node.options.find((o) => o.value === node.selectedValue);
        const title = opt?.title || opt?.highLightText;
        return title ? String(title).trim() : null;
    };
    let result = {
        text: [
            rule.selectedValue === "any_product"
                ? "Get"
                : rule.attributes === "bogo_buy_any" &&
                    rule.groupRule.selectedValue !== "any_product"
                    ? "Buy any"
                    : rule.title,
        ],
        bold: [
            (isOneLevelBoolean || isSelectBoolean)
                ? ""
                : rule.selectedValue
                ? rule.selectedValue === "any_product"
                    ? "Any Product"
                    : getSelectTitle(rule)
                        ? getSelectTitle(rule)
                    : rule.listType === "category"
                        ? getCategoryDisplayValue(rule.selectedValue, getCategoryById)
                        : rule.listType === "user_group"
                            ? getGroupDisplayValue(rule.selectedValue, getGroupById)
                            : `${rule.inputType === "dollar" ? "$" : ""}${formatValue(rule.selectedValue)}${rule.inputType === "percentage" ? "%" : ""}`
                : "",
        ],
    };

    if (rule.endTitle) {
        result.text.push(rule.endTitle);
        result.bold.push("");
    }

    if (rule.groupRule) {
        result.text.push(rule.groupRule.title);
        result.bold.push(
            rule.groupRule.selectedValue
                ? rule.groupRule.value === "on_cart_product"
                    ? cartProductOptions?.find(
                        (item) => item.value === rule.groupRule.selectedValue || 'cart_total',
                    )?.highLightText
                    : rule.groupRule.selectedValue === "any_product"
                        ? "Any Product"
                        : getSelectTitle(rule.groupRule)
                            ? getSelectTitle(rule.groupRule)
                        : rule?.groupRule.listType === "category"
                            ? getCategoryDisplayValue(rule?.groupRule.selectedValue, getCategoryById)
                            : rule?.groupRule.listType === "user_group"
                                ? getGroupDisplayValue(rule?.groupRule.selectedValue, getGroupById)
                                : `${rule?.groupRule.inputType === "dollar" ? "$" : ""}${formatValue(rule?.groupRule.selectedValue)} ${rule?.groupRule.inputType === "percentage" ? "%" : ""}`
                : "",
        );

        if (
            rule.groupRule.endTitle &&
            !["each_or_any"].includes(rule?.attributes)
        ) {
            result.text.push(rule.groupRule.endTitle);
            result.bold.push("");
        }

        if (rule.groupRule.groupRule) {

            result.text.push(rule.groupRule.groupRule.title);
            const gg = rule.groupRule.groupRule;
            const ggTitle = getSelectTitle(gg);
            result.bold.push(
                ggTitle ||
                `${gg.inputType === "dollar" ? "$" : ""}${formatValue(gg.selectedValue)}${gg.inputType === "percentage" ? "%" : ""}`,
            );

            if (rule.groupRule.groupRule.endTitle) {
                result.text.push(rule.groupRule.groupRule.endTitle);
                result.bold.push("");
            }

            // Handle the additional nested groupRule

            if (rule.groupRule.groupRule.groupRule) {
                const ggg = rule.groupRule.groupRule.groupRule;
                result.text.push(ggg.title === "on" ? "" : ggg.title);
                const gggTitle = getSelectTitle(ggg);
                result.bold.push(
                    gggTitle ||
                    `${ggg.inputType === "dollar" ? "$" : ""}${formatValue(ggg.selectedValue)}${ggg.inputType === "percentage" ? "%" : ""}`,
                );

                if (rule.groupRule.groupRule.groupRule.endTitle) {
                    result.text.push(rule.groupRule.groupRule.groupRule.endTitle);
                    result.bold.push("");
                }
            }
        }
    }

    return result;
}

export const formatRuleSetToSentence = ({
    ruleSet,
    getCategoryById = () => { },
    getGroupById = () => { },
}) => {
    const textParts = ["If ", "", " "];
    const normalizedRuleType = String(ruleSet?.ruleType || "").trim();
    const ruleTypeLabel =
        normalizedRuleType === "cart"
            ? "order"
            : normalizedRuleType.toUpperCase() === "BOGO"
                ? "BOGO Deals"
                : ruleSet?.ruleType;
    let boldParts = [
        "",
        ruleTypeLabel,
        "",
    ];

    ruleSet?.ifRules?.forEach((rule, index) => {
        const { text, bold } = formatRulePart(rule, getCategoryById, getGroupById);
        text.forEach((part, i) => {
            textParts.push(part + " ");
            boldParts.push(bold[i]?.trim() + " ");
        });
        if (index < ruleSet.ifRules.length - 1) {
            textParts.push("and ");
            boldParts.push("");
        }
    });

    textParts.push("Then ");
    boldParts.push("");

    ruleSet?.thenRules?.forEach((rule, index) => {
        const { text, bold } = formatRulePart(rule, getCategoryById, getGroupById);
        text.forEach((part, i) => {
            textParts.push(part + (part.includes("$") ? "" : " "));
            boldParts.push(bold[i] + " ");
        });
        if (index < ruleSet.thenRules.length - 1) {
            textParts.push("and ");
            boldParts.push("");
        }
    });

    boldParts = moveBoldTextForEachRule(boldParts);

    return { textParts, boldParts };
};

export function moveBoldTextForEachRule(arr) {
    // Create a copy of the array to avoid modifying the original
    let result = [...arr];

    // Loop through the array until second-to-last element
    for (let i = 0; i < result.length - 1; i++) {
        // Check if current element is "each"
        if (result?.[i]?.trim().toLowerCase() === "each") {
            // Store the next element
            let nextElement = result[i + 1];
            // Replace current "each" position with next element
            result[i] = nextElement;
            // Put "each" in next position
            result[i + 1] = "each";
            // Skip the next element since we just processed it
            i++;
        }
    }

    return result;
}

export const promoRuleOptions = [
    {
        iconColor: "#475cc7",
        icon: <Person fontSize="medium" />,
        title: "User",
        value: "user",
    },
    {
        iconColor: "#5dc07e",
        icon: <Category fontSize="medium" />,
        title: "Product",
        value: "product",
    },
    {
        iconColor: "#ff7e33",
        icon: <CartIcon />,
        title: "Cart",
        value: "cart",
    },
    {
        iconColor: "#0dcaf0",
        icon: <TimeIcon />,
        title: "Time",
        value: "time",
    },
    {
        iconColor: "#ad40a5",
        icon: <PaymentIcon />,
        title: "Payment",
        value: "payment",
    },
    {
        iconColor: "#e91e63",
        icon: <Redeem fontSize="medium" />,
        title: "BOGO",
        value: "BOGO",
    },
];



export const format_coupon_code = ({
    coupon_code,
    isAllowComma = true,
    isAllowAmbiguousCharacter = false,
}) => {
    if (!coupon_code) {
        return "";
    }

    const regexPattern = isAllowComma ? /[^a-zA-Z0-9,]/g : /[^a-zA-Z0-9]/g; // Adjust pattern based on isAllowComma
    let value = coupon_code
        ?.replace(regexPattern, "") // Remove all non-alphanumeric characters, allowing commas if isAllowComma is true
        ?.replace(/,+/g, ","); // Replace multiple consecutive commas with a single comma if isAllowComma is true

    value = isAllowAmbiguousCharacter ? value?.replace(/[iI10oO]/g, "") : value;

    return value;
};

export const maxNumberRegex = /^(?!0)\d{1,7}(\.\d{1,2})?$/;

export const getUniqueArrayData = (arr, key) => {
    const seen = new Map();
    return arr.filter((item) => {
        const value = item[key];
        if (!seen.has(value)) {
            seen.set(value, true);
            return true;
        }
        return false;
    });
};


export const allCategories = [
    {
        "id": "64703acd-88f1-438e-a849-56ece11150a4",
        "languages": [
            {
                "name": "Display Cards",
                "language": "en",
                "languageName": "English"
            },
            {
                "name": "Display Cards - fr",
                "language": "fr",
                "languageName": "French"
            }
        ],
        "name": "Display Cards"
    },
    {
        "id": "655f7af7-122e-41bf-9b26-8204654ed735",
        "languages": [
            {
                "name": "Brochure",
                "language": "en",
                "languageName": "English"
            },
            {
                "name": "Brochure-fr",
                "language": "fr",
                "languageName": "French"
            }
        ],
        "name": "Brochure"
    },
    {
        "id": "65dbb4d2-3152-472c-9dc5-601bcaa955c1",
        "languages": [
            {
                "name": "Point of Sale",
                "language": "en",
                "languageName": "English"
            },
            {
                "name": "Point of Sale - fr",
                "language": "fr",
                "languageName": "French"
            }
        ],
        "name": "Point of Sale"
    },
    {
        "id": "6acad5e9-0593-49e2-82ad-0061bb3f2c7b",
        "languages": [
            {
                "name": "SmartOne",
                "language": "en",
                "languageName": "English"
            },
            {
                "name": "SmartOne-fr",
                "language": "fr",
                "languageName": "French"
            }
        ],
        "name": "SmartOne"
    },
    {
        "id": "8e5e35b4-55a6-4ecf-bee7-805971bffb08",
        "languages": [
            {
                "name": "Test Category",
                "language": "en",
                "languageName": "English"
            },
            {
                "name": "CategoryTest",
                "language": "fr",
                "languageName": "French"
            }
        ],
        "name": "Test Category"
    }
]

export const userGroups = [
    {
        "id": "VIP Users",
        "created_at": "2025-01-29T10:19:26.071Z",
        "updated_at": "2025-02-07T11:40:41.307Z",
        "name": "VIP Users",
        "description": "",
        "status": "active",
        "group_users": null,
        "users": null,
        "count": 3
    },
    {
        "id": "Regional Users",
        "created_at": "2025-01-29T09:52:01.864Z",
        "updated_at": "2025-01-29T09:52:01.864Z",
        "name": "Regional Users",
        "description": "",
        "status": "active",
        "group_users": null,
        "users": null,
        "count": 1
    },
    {
        "id": "Indica",
        "created_at": "2025-01-29T09:52:01.824Z",
        "updated_at": "2025-01-29T09:52:01.864Z",
        "name": "Indica",
        "description": "",
        "status": "active",
        "group_users": null,
        "users": null,
        "count": 1
    },
    {
        "id": "Sativa",
        "created_at": "2025-01-29T09:52:01.864Z",
        "updated_at": "2025-01-29T09:52:01.864Z",
        "name": "Sativa",
        "description": "",
        "status": "active",
        "group_users": null,
        "users": null,
        "count": 1
    },
    {
        "id": "Hybrid",
        "created_at": "2025-01-29T09:52:01.864Z",
        "updated_at": "2025-01-29T09:52:01.864Z",
        "name": "Hybrid",
        "description": "",
        "status": "active",
        "group_users": null,
        "users": null,
        "count": 1
    }
]

export const _productsList = [
    {
        "id": "1370",
        "display_name": "QA Test Product Testing Product Test Product Testing Product Test Product Testing Product Test Product Testing Product",
        "name": "3 Piece Modular Sectional",
        "is_sale_item": true,
        "sale_price": 29.16,
        "type": "ecommerce",
        "SKU": "LGF51"
    },
    {
        "id": "9641",
        "display_name": "SmartOne Furniture Plus Activation Cards",
        "name": "SmartOne Furniture PLUS Activation Cards, 25 Pack",
        "is_sale_item": true,
        "sale_price": 100,
        "type": "ecommerce",
        "SKU": "PS7011-SFP"
    },
    {
        "id": "9636",
        "display_name": "Display: SmartOne Appliance 8.5 x 11 Table Top One Pager",
        "name": "Display: SmartOne Appliance 8.5 x 11 Table Top One Pager",
        "is_sale_item": true,
        "sale_price": 12.72,
        "type": "ecommerce",
        "SKU": "PS4203-SA"
    },
    {
        "id": "3564",
        "display_name": "SmartOne Test card",
        "name": "StainGuard Cotton Terry Mattress Protector - King",
        "is_sale_item": true,
        "sale_price": 3100.99,
        "type": "ecommerce",
        "SKU": "ZSG78"
    },
    {
        "id": "4619",
        "display_name": "SmartOne Appliance Lite Activation Card",
        "name": "SmartOne Appliance LITE Activation Cards, 10 Pack, Bi-Lingual",
        "is_sale_item": true,
        "sale_price": 70,
        "type": "ecommerce",
        "SKU": "PS8042-SL"
    },
    {
        "id": "172",
        "display_name": "SmartOne Appliance Activation Cards",
        "name": "SmartOne Appliance Activation Cards, 25 Pack",
        "is_sale_item": false,
        "sale_price": 3225,
        "type": "ecommerce",
        "SKU": "PS6014-SP"
    },
    {
        "id": "9635",
        "display_name": "SmartOne Furniture Poster",
        "name": "Display: SmartOne Poster - Furniture",
        "is_sale_item": false,
        "sale_price": 0,
        "type": "ecommerce",
        "SKU": "PS4102-SF"
    },
    {
        "id": "10155",
        "display_name": "SmartOne Combo Furniture + Furniture Plus Activation Cards",
        "name": "SmartOne Furniture & Furniture PLUS Combo Pack Activation Cards, 30/pack (15 Furniture & 15 Furniture Plus)",
        "is_sale_item": false,
        "sale_price": 2745,
        "type": "ecommerce",
        "SKU": "PS9501-CMB"
    },
    {
        "id": "9644",
        "display_name": "SmartOne Electronics Brochure",
        "name": "Brochure: SmartOne 5 Year Electronics, 25 per Bundle",
        "is_sale_item": false,
        "sale_price": 0,
        "type": "ecommerce",
        "SKU": "PSB9080-SE"
    },
    {
        "id": "9643",
        "display_name": "SmartOne Furniture Plus Brochures",
        "name": "Brochure: SmartOne Furniture Plus - 25 per Bundle",
        "is_sale_item": false,
        "sale_price": 0,
        "type": "ecommerce",
        "SKU": "PSB9070-SFP"
    }
]